import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { recipes as recipeApi, auth, labels as labelsApi, ai, groceries as groceriesApi, user as userApi } from '../utils/api';
import { Search, Plus, Filter, Grid, List, SortAsc, Trash2, ChefHat, X, Tag, Loader, User, LogOut, Timer, Printer, ChevronDown, ChevronRight, ChevronsUpDown, ShoppingCart, MessageCircle, Send, FileText, Shield, GripVertical, BookmarkPlus, FileUp, Scale, Calendar } from 'lucide-react';
import FilterBottomSheet from '../components/FilterBottomSheet';
import RecipeCard from '../components/RecipeCard';
import RecipeListItem from '../components/RecipeListItem';
import { categoryIcons } from '../components/RecipePlaceholder';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import HowHoneyWorks, { UpNextHelpTile, CookingHelpTile, SHOW_HELP_TILES_EVENT, HIDE_NEW_RECIPE_TIP_EVENT, SCROLL_TO_TOP_EVENT } from '../components/HowHoneyWorks';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { 
  trackCurrentlyCookingAdded, 
  trackCurrentlyCookingRemoved, 
  trackShoppingListOpened,
  trackShoppingListPrinted,
  trackMiseEnPlaceGenerated,
  trackRecipePrinted,
  trackRecipeMarkedCooked
} from '../utils/analytics';

// Timer launch helper - detects platform and launches native timer
const canLaunchNativeTimer = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/android/i.test(userAgent)) return 'android';
  if (navigator.platform && navigator.platform.indexOf('Win') !== -1) return 'windows';
  // iOS and macOS - no reliable native timer URL scheme from web
  return null;
};

const launchNativeTimer = (minutes, label) => {
  const platform = canLaunchNativeTimer();
  const seconds = minutes * 60;
  const truncatedLabel = label.length > 50 ? label.substring(0, 47) + '...' : label;
  
  try {
    switch (platform) {
      case 'android':
        window.location.href = `intent://timer?seconds=${seconds}&message=${encodeURIComponent(truncatedLabel)}#Intent;scheme=android-app;package=com.google.android.deskclock;end`;
        break;
      case 'windows':
        window.location.href = `ms-clock:timer?minutes=${minutes}&label=${encodeURIComponent(truncatedLabel)}`;
        break;
      default:
        return false;
    }
    return true;
  } catch (e) {
    console.error('Failed to launch timer:', e);
    return false;
  }
};

// Parse duration string to minutes (e.g., "5 min", "10-15 minutes" -> returns first number)
const parseDurationToMinutes = (durationStr) => {
  if (!durationStr) return null;
  const match = durationStr.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

// Timeline clock component - shows elapsed time with hour and minute hands
const TimelineClock = ({ startMin, size = 28 }) => {
  // Convert total minutes to hours and minutes
  const hours = Math.floor(startMin / 60);
  const minutes = startMin % 60;
  
  // Minute hand angle: 0 min = 12 o'clock = -90 degrees, each minute = 6 degrees
  const minuteAngle = (minutes * 6) - 90;
  
  // Hour hand angle: each hour = 30 degrees, plus offset for minutes (0.5 deg per minute)
  const hourAngle = (hours * 30) + (minutes * 0.5) - 90;
  
  const radius = size / 2 - 2;
  const cx = size / 2;
  const cy = size / 2;
  const hourHandLength = radius * 0.5;
  const minuteHandLength = radius * 0.75;
  
  // Calculate hand endpoints
  const hourRad = (hourAngle * Math.PI) / 180;
  const hourX = cx + hourHandLength * Math.cos(hourRad);
  const hourY = cy + hourHandLength * Math.sin(hourRad);
  
  const minuteRad = (minuteAngle * Math.PI) / 180;
  const minuteX = cx + minuteHandLength * Math.cos(minuteRad);
  const minuteY = cy + minuteHandLength * Math.sin(minuteRad);
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      {/* Clock face background */}
      <circle cx={cx} cy={cy} r={radius} fill="#fef3c7" stroke="#d97706" strokeWidth="1.5" />
      {/* Hour markers (12, 3, 6, 9) */}
      <circle cx={cx} cy={cy - radius + 3} r="1" fill="#92400e" /> {/* 12 */}
      <circle cx={cx + radius - 3} cy={cy} r="1" fill="#92400e" /> {/* 3 */}
      <circle cx={cx} cy={cy + radius - 3} r="1" fill="#92400e" /> {/* 6 */}
      <circle cx={cx - radius + 3} cy={cy} r="1" fill="#92400e" /> {/* 9 */}
      {/* Hour hand (shorter, thicker) */}
      <line x1={cx} y1={cy} x2={hourX} y2={hourY} stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" />
      {/* Minute hand (longer, thinner) */}
      <line x1={cx} y1={cy} x2={minuteX} y2={minuteY} stroke="#b45309" strokeWidth="1.5" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx={cx} cy={cy} r="2" fill="#92400e" />
    </svg>
  );
};

// Helper to convert decimal quantities to fractions (or pass through existing fractions)
const formatQuantity = (qty) => {
  if (!qty) return '';
  
  const qtyStr = String(qty).trim();
  
  // If it's already a fraction string (e.g., "1/2", "3/4"), return as-is
  if (/^\d+\/\d+$/.test(qtyStr)) {
    return qtyStr;
  }
  
  // If it's a mixed number (e.g., "1 1/2"), return as-is
  if (/^\d+\s+\d+\/\d+$/.test(qtyStr)) {
    return qtyStr;
  }
  
  // Try to parse as a number for decimal conversion
  const num = parseFloat(qtyStr);
  if (isNaN(num)) return qtyStr;
  
  const fractions = {
    0.125: '1/8', 0.25: '1/4', 0.33: '1/3', 0.333: '1/3', 
    0.5: '1/2', 0.66: '2/3', 0.666: '2/3', 0.67: '2/3',
    0.75: '3/4', 0.875: '7/8'
  };
  
  const whole = Math.floor(num);
  const decimal = num - whole;
  const roundedDecimal = Math.round(decimal * 1000) / 1000;
  
  let fractionStr = '';
  for (const [dec, frac] of Object.entries(fractions)) {
    if (Math.abs(roundedDecimal - parseFloat(dec)) < 0.02) {
      fractionStr = frac;
      break;
    }
  }
  
  if (whole > 0 && fractionStr) return `${whole} ${fractionStr}`;
  if (fractionStr) return fractionStr;
  if (whole > 0 && decimal === 0) return String(whole);
  return qtyStr;
};

// Words to skip when matching ingredients (preparation methods, adjectives, etc.)
const SKIP_WORDS = new Set(['large', 'small', 'medium', 'fresh', 'dried', 'ground', 'chopped', 'minced', 'sliced', 'diced', 'whole', 'hot', 'cold', 'warm', 'raw', 'cooked', 'baking', 'cooking', 'mixing', 'salad', 'prepared', 'unsalted', 'low-sodium', 'frozen', 'crumbled', 'baby', 'kosher', 'cloves', 'leaves', 'shredded', 'grated', 'softened', 'melted', 'cubed', 'julienned', 'quartered', 'halved', 'crushed', 'toasted', 'roasted', 'blanched', 'peeled', 'cored', 'seeded', 'deveined', 'trimmed', 'torn', 'packed', 'divided', 'beaten', 'whisked']);

// Generic words that are too common to match on their own
// These require at least one other distinctive word from the ingredient to match
const GENERIC_WORDS = new Set(['cheese', 'oil', 'sauce', 'butter', 'milk', 'cream', 'salt', 'pepper', 'sugar', 'flour', 'water']);

// Helper function to escape special regex characters
const escapeRegexWord = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Helper function to extract matching words from ingredient name
// Returns { allWords: [...], distinctiveWords: [...] }
const getIngredientMatchWords = (ingredientName) => {
  // Remove parenthetical content like "(cannellini or great northern)"
  const cleanName = ingredientName
    .replace(/\s*\([^)]*\)/g, '')  // Remove (content)
    .replace(/[()[\]{}]/g, '')      // Remove any remaining brackets
    .toLowerCase()
    .trim();
  
  // Split into words and filter out skip words
  const allWords = cleanName.split(/\s+/).filter(w => w.length >= 3 && !SKIP_WORDS.has(w));
  
  // Distinctive words are those NOT in GENERIC_WORDS
  const distinctiveWords = allWords.filter(w => !GENERIC_WORDS.has(w));
  
  return { allWords, distinctiveWords, cleanName };
};

// Check if step text should match an ingredient (smarter matching)
const shouldMatchIngredient = (stepTextLower, ingredientName, stepHasSoupContext = false) => {
  const cleanIngName = ingredientName.toLowerCase().replace(/\s*\([^)]*\)/g, '').trim();
  const isSoupIngredient = cleanIngName.includes(' soup');
  
  // RULE 1: Non-soup ingredient vs soup step
  // E.g., ingredient "cheddar cheese" should NOT match step "add the cheddar cheese soup"
  if (!isSoupIngredient && stepHasSoupContext) {
    // Check if the ingredient name appears followed by "soup" in the step
    const escapedName = escapeRegexWord(cleanIngName);
    const soupPattern = new RegExp(`\\b${escapedName}\\s+soup\\b`, 'i');
    if (soupPattern.test(stepTextLower)) {
      return false;
    }
  }
  
  return true;
};

// Helper function to find ingredients mentioned in step text
// Now accepts a single recipe's ingredients array (not all recipes)
const findIngredientsInStep = (stepText, ingredients) => {
  if (!stepText || !ingredients || ingredients.length === 0) return [];
  
  // Skip recipe overview items
  if (/\*\*.*\*\*\s*\(\d+\s*min/i.test(stepText) || /min total/i.test(stepText)) {
    return [];
  }
  
  const textLower = stepText.toLowerCase();
  const stepHasSoupContext = textLower.includes(' soup');
  const matchedIngredients = [];
  const matchedNames = new Set(); // Track which ingredient names have been matched
  
  // First pass: exact/full name matches (most specific)
  ingredients.forEach(ing => {
    if (!ing.name) return;
    const { cleanName } = getIngredientMatchWords(ing.name);
    
    if (cleanName.length < 3) return;
    
    // Check if this ingredient should match this step
    if (!shouldMatchIngredient(textLower, ing.name, stepHasSoupContext)) return;
    
    // Full name match
    if (textLower.includes(cleanName)) {
      matchedIngredients.push(ing);
      matchedNames.add(cleanName);
      return;
    }
    
    // For soup ingredients, also try matching without "soup" suffix
    // E.g., "cream of chicken soup" should match "cream of chicken"
    if (cleanName.includes(' soup')) {
      const withoutSoup = cleanName.replace(/\s+soup$/, '');
      if (textLower.includes(withoutSoup)) {
        matchedIngredients.push(ing);
        matchedNames.add(cleanName);
      }
    }
  });
  
  // Second pass: word-level matching - skip if already matched
  ingredients.forEach(ing => {
    if (!ing.name) return;
    const { allWords, distinctiveWords, cleanName } = getIngredientMatchWords(ing.name);
    
    // Skip if already matched by full name
    if (matchedNames.has(cleanName)) return;
    
    // Skip soup ingredients in second pass - they should only match by full name
    if (cleanName.includes(' soup')) return;
    
    // Check if this ingredient should match this step
    if (!shouldMatchIngredient(textLower, ing.name, stepHasSoupContext)) return;
    
    // If ingredient has distinctive words, require at least one to match
    // This prevents "creamy mozzarella cheese" from matching just because "cheese" appears
    if (distinctiveWords.length > 0) {
      // Must match at least one distinctive word
      let hasDistinctiveMatch = false;
      for (const word of distinctiveWords) {
        const regex = new RegExp(`\\b${escapeRegexWord(word)}s?\\b`, 'i');
        if (regex.test(textLower)) {
          hasDistinctiveMatch = true;
          break;
        }
      }
      
      if (hasDistinctiveMatch) {
        // Skip if a more specific ingredient already matched
        const moreSpecificAlreadyMatched = [...matchedNames].some(matchedName => 
          distinctiveWords.some(word => matchedName.includes(word))
        );
        if (!moreSpecificAlreadyMatched) {
          matchedIngredients.push(ing);
          matchedNames.add(cleanName);
        }
      }
    } else if (allWords.length > 0) {
      // No distinctive words, fall back to matching any word (e.g., single-word ingredients)
      for (const word of allWords) {
        const moreSpecificAlreadyMatched = [...matchedNames].some(matchedName => 
          matchedName.includes(word)
        );
        if (moreSpecificAlreadyMatched) continue;
        
        const regex = new RegExp(`\\b${escapeRegexWord(word)}s?\\b`, 'i');
        if (regex.test(textLower)) {
          matchedIngredients.push(ing);
          matchedNames.add(cleanName);
          break;
        }
      }
    }
  });
  
  return matchedIngredients;
};

// Helper function to find the matching recipe from currentMeal based on parsed recipe name
const findMatchingRecipe = (parsedRecipeName, currentMealRecipes) => {
  if (!parsedRecipeName || !currentMealRecipes) return null;
  
  // Remove emoji, bold markers, and parenthetical notes like "(while cookies bake)"
  const normalizedParsed = parsedRecipeName
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Remove emojis
    .replace(/\*\*/g, '')  // Remove bold markers
    .replace(/\s*\([^)]*\)\s*/g, ' ')  // Remove parenthetical notes
    .trim()
    .toLowerCase();
  
  // Extract core words (remove common prefixes like "classic", "simple", "easy", etc.)
  const getCoreWords = (str) => {
    const skipPrefixes = ['classic', 'simple', 'easy', 'quick', 'best', 'homemade', 'traditional', 'authentic', 'dairy-free', 'dairy free', 'gluten-free', 'gluten free', 'vegan'];
    let words = str.toLowerCase().split(/\s+/);
    // Remove skip prefixes from the beginning
    while (words.length > 1 && skipPrefixes.some(p => words[0] === p || words[0].startsWith(p))) {
      words.shift();
    }
    return words.join(' ');
  };
  
  // Get significant words (nouns) for matching
  const getSignificantWords = (str) => {
    const skipWords = ['the', 'a', 'an', 'with', 'and', 'or', 'in', 'on', 'for', 'to', 'of', 'from', 'boneless', 'skinless', 'skillet', 'pan', 'fried', 'baked', 'roasted', 'grilled'];
    return str.toLowerCase().split(/\s+/).filter(w => w.length >= 3 && !skipWords.includes(w));
  };
  
  const parsedCore = getCoreWords(normalizedParsed);
  const parsedWords = getSignificantWords(normalizedParsed);
  
  // Find best match - try multiple strategies
  let bestMatch = null;
  let bestScore = 0;
  
  currentMealRecipes.forEach(r => {
    const normalizedTitle = r.title?.toLowerCase().trim() || '';
    const titleCore = getCoreWords(normalizedTitle);
    const titleWords = getSignificantWords(normalizedTitle);
    
    let score = 0;
    
    // Strategy 1: Exact match (highest score)
    if (normalizedTitle === normalizedParsed) score = 100;
    
    // Strategy 2: Core words match
    else if (titleCore === parsedCore) score = 90;
    
    // Strategy 3: One contains the other
    else if (normalizedParsed.includes(normalizedTitle) || normalizedTitle.includes(normalizedParsed)) score = 80;
    
    // Strategy 4: Core words contain each other
    else if (parsedCore.includes(titleCore) || titleCore.includes(parsedCore)) score = 70;
    
    // Strategy 5: Count matching significant words (for partial matches like "Lemon Herb Chicken" vs "Lemon Herb Skillet Boneless Chicken Thighs")
    else {
      const matchingWords = parsedWords.filter(w => titleWords.some(tw => tw.includes(w) || w.includes(tw)));
      if (matchingWords.length >= 2) {
        score = 50 + (matchingWords.length * 5);
      }
    }
    
    // Strategy 6: First significant word matches
    if (score === 0) {
      const parsedFirstWord = parsedCore.split(/\s+/)[0];
      const titleFirstWord = titleCore.split(/\s+/)[0];
      if (parsedFirstWord.length >= 4 && (titleCore.includes(parsedFirstWord) || parsedCore.includes(titleFirstWord))) {
        score = 40;
      }
    }
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = r;
    }
  });
  
  return bestMatch;
};

// Mise en Place Content Component - handles ingredient scoping and "show once" logic
const MiseEnPlaceContent = ({ content, currentMealRecipes, miseCheckedItems, setMiseCheckedItems, miseScrollRef }) => {
  // Parse markdown and pre-compute all ingredient assignments with useMemo
  // NEW APPROACH: Go from ingredients → steps (not steps → ingredients)
  const { phases, maxTime, stepIngredientMap, parseError } = useMemo(() => {
    try {
      const lines = content.split('\n');
      const parsedPhases = [];
      let currentPhase = null;
      let currentRecipe = null;
      
      // First pass: Parse all phases, recipes, and steps
      // Also collect all steps per recipe for ingredient assignment
      const allStepsPerRecipe = {}; // recipeName -> [{stepContent, stepKey}]
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      // Main title (## Mise en Place Timeline)
      if (trimmed.startsWith('## ')) {
        if (currentPhase) parsedPhases.push(currentPhase);
        currentPhase = { type: 'title', content: trimmed.replace('## ', ''), recipes: [] };
        currentRecipe = null;
      }
      // Phase header (### ⏱️ Minute... or ### 📋 or ### ✅)
      else if (trimmed.startsWith('### ')) {
        if (currentPhase) parsedPhases.push(currentPhase);
        currentPhase = { type: 'phase', content: trimmed.replace('### ', ''), recipes: [] };
        currentRecipe = null;
      }
      // Recipe name - bold text (with or without emoji) that's not a list item
      // Match: **Recipe Name** or **🍤 Recipe Name** or 🍤 Recipe Name
      else if (
        // Bold text with emoji
        (/^\*\*[^\*]+\*\*/.test(trimmed) && /[🍳🥗🍪🧇🍞🥘🍰🍖🍝🥣🥬🍹🥧🍕🍔🌮🥪🥙🧆🍜🍲🥡🍛🍣🍤🥟🥠🍚🍙🍘🍥🥮🍡🍢🍧🍨🍦🥧🧁🍰🎂🍮🍭🍬🍫🍿🍩🍪☕🍵🧃🥤🧋🍶🍺🍻🥂🍷🥃🍸🍹🧉🍾🧊]/.test(trimmed)) ||
        // Emoji at start followed by text
        (/^[🍳🥗🍪🧇🍞🥘🍰🍖🍝🥣🥬🍹🥧🍕🍔🌮🥪🥙🧆🍜🍲🥡🍛🍣🍤🥟🥠🍚🍙🍘🍥🥮🍡🍢🍧🍨🍦🥧🧁🍰🎂🍮🍭🍬🍫🍿🍩🍪☕🍵🧃🥤🧋🍶🍺🍻🥂🍷🥃🍸🍹🧉🍾🧊]\s*\w/.test(trimmed) && !trimmed.startsWith('- ') && !/^\[\d+\]/.test(trimmed)) ||
        // Bold text WITHOUT emoji - but must be after a phase header (currentPhase exists) and not a list item
        (/^\*\*[^\*]+\*\*$/.test(trimmed) && currentPhase && !trimmed.includes('min total'))
      ) {
        const recipeName = trimmed.replace(/\*\*/g, '');
        // Normalize recipe name for matching: strip emojis, parenthetical notes like "(while X cooks)", and trim
        const normalizedName = recipeName
          .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Remove emojis
          .replace(/\s*\([^)]*\)\s*/g, ' ')  // Remove parenthetical notes
          .trim();
        currentRecipe = { name: recipeName, normalizedName: normalizedName, steps: [] };
        if (currentPhase) currentPhase.recipes.push(currentRecipe);
        
        // Initialize step collection for this recipe using NORMALIZED name
        if (!allStepsPerRecipe[normalizedName]) {
          allStepsPerRecipe[normalizedName] = [];
        }
      }
      // Step (starts with - or *)
      else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const stepContent = trimmed.replace(/^[-*]\s*/, '');
        if (currentRecipe) {
          currentRecipe.steps.push(stepContent);
          
          // Store step for later ingredient assignment - use NORMALIZED name for consistent keying
          const stepKey = `${currentRecipe.normalizedName}::${stepContent.slice(0, 50)}`;
          allStepsPerRecipe[currentRecipe.normalizedName].push({
            stepContent: stepContent.replace(/\*\*/g, '').toLowerCase(),
            stepKey
          });
        } else if (currentPhase) {
          if (!currentPhase.extra) currentPhase.extra = [];
          currentPhase.extra.push(stepContent);
        }
      }
      // Other content
      else if (currentPhase) {
        if (currentPhase.recipes.length > 0) {
          if (!currentPhase.extraAfter) currentPhase.extraAfter = [];
          currentPhase.extraAfter.push(trimmed);
        } else {
          if (!currentPhase.extra) currentPhase.extra = [];
          currentPhase.extra.push(trimmed);
        }
      }
    });
    if (currentPhase) parsedPhases.push(currentPhase);
    
    // Second pass: Assign ingredients to steps
    // Go from INGREDIENTS → STEPS (process ingredients in recipe order)
    // RULE: Ingredient N can only go to steps at or after where ingredient N-1 was assigned
    // RULE: Each recipe's ingredients stay with that recipe's steps ONLY
    // RULE: Each ingredient can only be assigned ONCE per actual recipe (even across multiple parsed names)
    const ingredientMap = {}; // stepKey -> [ingredients]
    
    // Track which ingredients have been assigned per ACTUAL recipe (by recipe title)
    // This prevents duplicates when same recipe appears multiple times (e.g., "Kale Salad" and "Kale Salad (passive)")
    const assignedIngredientsPerRecipe = {}; // recipeTitle -> Set of "qty_unit_name" keys
    
    // First, collect ALL steps for each actual recipe across ALL its parsed names
    const stepsPerActualRecipe = {}; // recipeTitle -> [{stepContent, stepKey, parsedRecipeName}]
    const parsedNameToActualTitle = {}; // Map parsed names to actual recipe titles
    
    Object.keys(allStepsPerRecipe).forEach(parsedRecipeName => {
      const matchingRecipe = findMatchingRecipe(parsedRecipeName, currentMealRecipes || []);
      if (!matchingRecipe) {
        return;
      }
      
      const recipeTitle = matchingRecipe.title;
      parsedNameToActualTitle[parsedRecipeName] = recipeTitle;
      
      if (!stepsPerActualRecipe[recipeTitle]) {
        stepsPerActualRecipe[recipeTitle] = [];
      }
      
      // Add all steps from this parsed name to the actual recipe's step list
      // IMPORTANT: Rebuild stepKey using ACTUAL recipe title for consistent lookup
      const steps = allStepsPerRecipe[parsedRecipeName] || [];
      steps.forEach(step => {
        const newStepKey = `${recipeTitle}::${step.stepContent.slice(0, 50)}`;
        stepsPerActualRecipe[recipeTitle].push({
          ...step,
          stepKey: newStepKey,
          originalParsedName: parsedRecipeName
        });
      });
    });
    
    // Now process each actual recipe's ingredients against ALL its steps
    currentMealRecipes?.forEach(recipe => {
      if (!recipe.ingredients) return;
      
      const recipeTitle = recipe.title;
      const allSteps = stepsPerActualRecipe[recipeTitle] || [];
      if (allSteps.length === 0) {
        return;
      }
      
      // Initialize tracking for this recipe
      if (!assignedIngredientsPerRecipe[recipeTitle]) {
        assignedIngredientsPerRecipe[recipeTitle] = new Set();
      }
      
      // Helper function to check if a step mentions an ingredient
      const doesStepMatchIngredient = (stepContent, cleanIngName, allWords, distinctiveWords) => {
        // Full clean name match (highest priority)
        if (stepContent.includes(cleanIngName)) {
          return true;
        }
        
        // For soup ingredients, also try matching without "soup" suffix
        if (cleanIngName.includes(' soup')) {
          const withoutSoup = cleanIngName.replace(/\s+soup$/, '');
          if (stepContent.includes(withoutSoup)) {
            return true;
          }
        }
        
        // If has distinctive words, require at least one to match
        if (distinctiveWords.length > 0) {
          for (const word of distinctiveWords) {
            const regex = new RegExp(`\\b${escapeRegexWord(word)}s?\\b`, 'i');
            if (regex.test(stepContent)) return true;
          }
          return false;
        }
        
        // No distinctive words - check all words (single-word ingredients like "broth")
        if (allWords.length >= 1) {
          for (const word of allWords) {
            const regex = new RegExp(`\\b${escapeRegexWord(word)}s?\\b`, 'i');
            if (regex.test(stepContent)) return true;
          }
        }
        return false;
      };
      
      // Helper function to find matching step for an ingredient
      const findMatchingStep = (ing, ingIndex, startFromStep) => {
        const { allWords, distinctiveWords, cleanName } = getIngredientMatchWords(ing.name);
        
        for (let stepIdx = startFromStep; stepIdx < allSteps.length; stepIdx++) {
          const { stepContent, stepKey } = allSteps[stepIdx];
          if (doesStepMatchIngredient(stepContent, cleanName, allWords, distinctiveWords)) {
            return { stepKey, stepIdx };
          }
        }
        return null;
      };
      
      // ============ PASS 1: Respect ingredient order ============
      // Process ingredients in recipe order, each can only go to steps >= last assigned step
      let lastAssignedStepIndex = 0;
      
      recipe.ingredients.forEach((ing, ingIndex) => {
        if (!ing.name) return;
        
        const ingUniqueKey = `${ing.quantity || ''}_${ing.unit || ''}_${ing.name.toLowerCase()}`;
        if (assignedIngredientsPerRecipe[recipeTitle].has(ingUniqueKey)) return;
        
        const match = findMatchingStep(ing, ingIndex, lastAssignedStepIndex);
        
        if (match) {
          if (!ingredientMap[match.stepKey]) {
            ingredientMap[match.stepKey] = [];
          }
          ingredientMap[match.stepKey].push({ ...ing, _ingIndex: ingIndex });
          assignedIngredientsPerRecipe[recipeTitle].add(ingUniqueKey);
          lastAssignedStepIndex = match.stepIdx;
        }
      });
      
      // ============ PASS 2: Pick up stragglers ============
      // Any unassigned ingredients get a second chance, searching ALL steps from the beginning
      recipe.ingredients.forEach((ing, ingIndex) => {
        if (!ing.name) return;
        
        const ingUniqueKey = `${ing.quantity || ''}_${ing.unit || ''}_${ing.name.toLowerCase()}`;
        if (assignedIngredientsPerRecipe[recipeTitle].has(ingUniqueKey)) return; // Already assigned in Pass 1
        
        // Search from step 0 (no constraint)
        const match = findMatchingStep(ing, ingIndex, 0);
        
        if (match) {
          if (!ingredientMap[match.stepKey]) {
            ingredientMap[match.stepKey] = [];
          }
          ingredientMap[match.stepKey].push({ ...ing, _ingIndex: ingIndex });
          assignedIngredientsPerRecipe[recipeTitle].add(ingUniqueKey);
        }
      });
    });
    
    // Sort ingredients in each step by their original recipe index
    Object.keys(ingredientMap).forEach(stepKey => {
      ingredientMap[stepKey].sort((a, b) => a._ingIndex - b._ingIndex);
      // Remove the temporary _ingIndex property
      ingredientMap[stepKey] = ingredientMap[stepKey].map(({ _ingIndex, ...ing }) => ing);
    });
    
    // Calculate total time
    const totalTime = Math.max(
      ...(currentMealRecipes || []).map(r => (r.prep_time || 0) + (r.cook_time || 0)),
      0
    );
    
    return { phases: parsedPhases, maxTime: totalTime, stepIngredientMap: ingredientMap, parseError: null };
    } catch (err) {
      console.error('MiseEnPlaceContent parse error:', err);
      return { phases: [], maxTime: 0, stepIngredientMap: {}, parseError: err.message };
    }
  }, [content, currentMealRecipes]);
  
  // If there was a parse error, show raw content as fallback
  if (parseError) {
    return (
      <div className="mise-en-place-content">
        <div className="text-red-500 mb-4 p-2 bg-red-50 rounded">Error: {parseError}</div>
        <pre className="whitespace-pre-wrap text-sm text-gray-700">{content}</pre>
      </div>
    );
  }
  
  return (
    <div className="mise-en-place-content">
      {phases.map((phase, phaseIdx) => {
        if (phase.type === 'title') {
          // Replace "Mise en Place" with "Concurrent Cooking" in the title
          const displayTitle = phase.content.replace(/Mise en Place/gi, 'Concurrent Cooking');
          return (
            <h2 key={phaseIdx} className="text-xl font-bold text-gray-900 mt-2 mb-4">
              {displayTitle} {maxTime > 0 && <span className="font-normal text-gray-500">(~{maxTime} minutes total)</span>}
            </h2>
          );
        }
        
        // Parse phase header for time info
        const timeMatch = phase.content.match(/Minute\s+(\d+)[–-](\d+)/i);
        const titleMatch = phase.content.match(/[–—-]\s*(.+)$/);
        const startMin = timeMatch ? parseInt(timeMatch[1]) : 0;
        const endMin = timeMatch ? parseInt(timeMatch[2]) : 0;
        const duration = endMin - startMin;
        const title = titleMatch ? titleMatch[1].trim().replace(/^\d+\s*[–—-]\s*/, '') : phase.content;
        
        // Count non-title phases for step numbering
        const stepNumber = phases.slice(0, phaseIdx).filter(p => p.type !== 'title').length + 1;
        
        return (
          <div key={phaseIdx} className="mb-6">
            {/* Phase Header */}
            <div className="mt-6 mb-3 pb-2 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-amber-600 text-white text-sm font-bold rounded-full flex-shrink-0">{stepNumber}</span>
                  {timeMatch && (
                    <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500">
                      <TimelineClock startMin={startMin} size={24} />
                      <span>@ {startMin} min</span>
                    </div>
                  )}
                  <span className="text-base sm:text-lg font-semibold text-gray-800">{title}</span>
                </div>
                {duration > 0 && <span className="text-xs sm:text-sm text-gray-500 ml-8 sm:ml-0">Duration: {duration} minutes</span>}
              </div>
            </div>
            
            {/* Extra content (Recipes Overview) - strip markdown bold markers, smaller font */}
            {phase.extra && phase.extra.length > 0 && (
              <div className="text-gray-600 mb-4 text-sm">
                {phase.extra.map((line, i) => <p key={i}>{line.replace(/\*\*/g, '')}</p>)}
              </div>
            )}
            
            {/* Recipes Grid */}
            {phase.recipes.length > 0 && (
              <div className={`grid gap-6 ${
                phase.recipes.length === 1 ? 'grid-cols-1' : 
                phase.recipes.length === 2 ? 'md:grid-cols-2' : 
                'md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {phase.recipes.map((recipe, recipeIdx) => (
                  <div key={recipeIdx} className="min-w-0 bg-gray-50 rounded-lg p-4">
                    <p className="text-amber-600 font-semibold mb-2">{recipe.name}</p>
                    
                    <ul className="space-y-3">
                      {recipe.steps.map((step, stepIdx) => {
                        const stepText = step.replace(/^\[\d+\]\s*/, '');
                        const stepNum = step.match(/^\[(\d+)\]/)?.[1];
                        const itemKey = `mise-${step.slice(0, 50).replace(/\s+/g, '-').toLowerCase()}`;
                        const isChecked = miseCheckedItems[itemKey] || false;
                        
                        // Parse duration
                        const durationMatch = step.match(/\((\d+(?:-\d+)?\s*min(?:utes?)?)\)\s*$/i);
                        const stepDuration = durationMatch ? durationMatch[1] : null;
                        
                        // Get pre-computed ingredients from the map (for pills - "show once" logic)
                        // Use the ACTUAL recipe title (from findMatchingRecipe) for consistent stepKey lookup
                        const actualRecipe = findMatchingRecipe(recipe.name, currentMealRecipes);
                        const actualRecipeTitle = actualRecipe?.title || recipe.name;
                        // stepKey uses lowercase stepContent (as created during ingredient assignment)
                        const stepContentLower = step.replace(/\*\*/g, '').toLowerCase();
                        const stepKey = `${actualRecipeTitle}::${stepContentLower.slice(0, 50)}`;
                        const matchedIngredients = stepIngredientMap[stepKey] || [];
                        
                        // Get ALL ingredients for highlighting
                        const allRecipeIngredients = actualRecipe?.ingredients || [];
                        
                        const handleCheck = () => {
                          const scrollTop = miseScrollRef.current?.scrollTop || 0;
                          setMiseCheckedItems(prev => {
                            setTimeout(() => {
                              if (miseScrollRef.current) miseScrollRef.current.scrollTop = scrollTop;
                            }, 0);
                            return { ...prev, [itemKey]: !prev[itemKey] };
                          });
                        };
                        
                        // Highlight ALL ingredients from this recipe that appear in step text
                        // First strip any existing ** markers, then add our own
                        let highlightedText = stepText.replace(/\*\*/g, '');
                        const cleanStepText = highlightedText.toLowerCase();
                        const stepHasSoupContext = cleanStepText.includes(' soup');
                        
                        allRecipeIngredients.forEach(ing => {
                          if (!ing.name) return;
                          
                          // Check if this ingredient should match this step
                          if (!shouldMatchIngredient(cleanStepText, ing.name, stepHasSoupContext)) return;
                          
                          // Get match words info
                          const { allWords, distinctiveWords, cleanName } = getIngredientMatchWords(ing.name);
                          
                          // Try full name match first
                          if (cleanStepText.includes(cleanName)) {
                            const escapedName = cleanName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const regex = new RegExp(`(${escapedName})`, 'gi');
                            highlightedText = highlightedText.replace(regex, '**$1**');
                            return;
                          }
                          
                          // For soup ingredients, also try matching without "soup" suffix
                          if (cleanName.includes(' soup')) {
                            const withoutSoup = cleanName.replace(/\s+soup$/, '');
                            if (cleanStepText.includes(withoutSoup)) {
                              const escapedName = withoutSoup.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                              const regex = new RegExp(`(${escapedName})`, 'gi');
                              highlightedText = highlightedText.replace(regex, '**$1**');
                              return;
                            }
                            // Don't do word-level matching for soup ingredients
                            return;
                          }
                          
                          // Try matching distinctive words first
                          if (distinctiveWords.length > 0) {
                            for (const word of distinctiveWords) {
                              if (cleanStepText.includes(word)) {
                                const regex = new RegExp(`\\b(${escapeRegexWord(word)}s?)\\b`, 'gi');
                                highlightedText = highlightedText.replace(regex, '**$1**');
                                break;
                              }
                            }
                          } else if (allWords.length > 0) {
                            // Fall back to all words for single-word ingredients
                            for (const word of allWords) {
                              if (cleanStepText.includes(word)) {
                                const regex = new RegExp(`\\b(${escapeRegexWord(word)}s?)\\b`, 'gi');
                                highlightedText = highlightedText.replace(regex, '**$1**');
                                break;
                              }
                            }
                          }
                        });
                        
                        return (
                          <li key={stepIdx} className="flex items-start gap-3 text-gray-700 text-sm">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={handleCheck}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer flex-shrink-0"
                            />
                            <div className="flex-1">
                              <span className={isChecked ? 'line-through text-gray-400' : ''}>
                                {stepNum && <span className="text-gray-400">[{stepNum}] </span>}
                                {highlightedText.split('**').map((part, i) => 
                                  i % 2 === 1 ? <strong key={i} className="text-amber-700">{part}</strong> : part
                                )}
                              </span>
                              {stepDuration && (
                                canLaunchNativeTimer() ? (
                                  <button
                                    onClick={() => {
                                      const mins = parseDurationToMinutes(stepDuration);
                                      if (mins) launchNativeTimer(mins, stepText.substring(0, 50));
                                    }}
                                    className="flex items-center gap-1 mt-1 text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
                                    title={`Set ${stepDuration} timer`}
                                  >
                                    <Timer size={14} />
                                    <span className="text-xs underline">{stepDuration}</span>
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-1 mt-1 text-gray-500">
                                    <Timer size={14} />
                                    <span className="text-xs">{stepDuration}</span>
                                  </div>
                                )
                              )}
                              {matchedIngredients.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {matchedIngredients.map((ing, idx) => {
                                    const formattedQty = formatQuantity(ing.quantity);
                                    const hasQty = formattedQty && formattedQty !== 'null' && formattedQty.trim();
                                    const hasUnit = ing.unit && ing.unit !== 'null' && ing.unit.trim();
                                    // Abbreviate common units to save space
                                    const abbreviateUnit = (unit) => {
                                      if (!unit) return '';
                                      const abbrevs = {
                                        'teaspoon': 'tsp', 'teaspoons': 'tsp',
                                        'tablespoon': 'Tbsp', 'tablespoons': 'Tbsp',
                                        'ounce': 'oz', 'ounces': 'oz',
                                        'pound': 'lb', 'pounds': 'lbs',
                                        'cup': 'cup', 'cups': 'cups'
                                      };
                                      return abbrevs[unit.toLowerCase()] || unit;
                                    };
                                    const displayUnit = hasUnit ? abbreviateUnit(ing.unit) : '';
                                    const qtyText = (hasQty || displayUnit) ? `${hasQty ? formattedQty : ''}${hasQty && displayUnit ? ' ' : ''}${displayUnit} ` : '';
                                    // Include preparation in parentheses if present
                                    const prepText = ing.preparation ? ` (${ing.preparation})` : '';
                                    return (
                                      <span key={idx} className="inline-flex items-center px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                                        {qtyText}{ing.name}{prepText}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            
            {/* Extra content after recipes - render with checkboxes like regular steps */}
            {phase.extraAfter && phase.extraAfter.length > 0 && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <ul className="space-y-3">
                  {phase.extraAfter.map((line, i) => {
                    const stepText = line.replace(/^\[\d+\]\s*/, '').replace(/\*\*/g, '');
                    const stepNum = line.match(/^\[(\d+)\]/)?.[1];
                    const itemKey = `mise-extra-${phaseIdx}-${i}-${line.slice(0, 30).replace(/\s+/g, '-').toLowerCase()}`;
                    const isChecked = miseCheckedItems[itemKey] || false;
                    
                    // Parse duration if present
                    const durationMatch = line.match(/\((\d+(?:-\d+)?\s*min(?:utes?)?)\)\s*$/i);
                    const stepDuration = durationMatch ? durationMatch[1] : null;
                    
                    const handleCheck = () => {
                      const scrollTop = miseScrollRef.current?.scrollTop || 0;
                      setMiseCheckedItems(prev => {
                        setTimeout(() => {
                          if (miseScrollRef.current) miseScrollRef.current.scrollTop = scrollTop;
                        }, 0);
                        return { ...prev, [itemKey]: !prev[itemKey] };
                      });
                    };
                    
                    return (
                      <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={handleCheck}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer flex-shrink-0"
                        />
                        <div className="flex-1">
                          <span className={isChecked ? 'line-through text-gray-400' : ''}>
                            {stepNum && <span className="text-gray-400">[{stepNum}] </span>}
                            {stepText}
                          </span>
                          {stepDuration && (
                            canLaunchNativeTimer() ? (
                              <button
                                onClick={() => {
                                  const mins = parseDurationToMinutes(stepDuration);
                                  if (mins) launchNativeTimer(mins, stepText.substring(0, 50));
                                }}
                                className="flex items-center gap-1 mt-1 text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
                                title={`Set ${stepDuration} timer`}
                              >
                                <Timer size={14} />
                                <span className="text-xs underline">{stepDuration}</span>
                              </button>
                            ) : (
                              <div className="flex items-center gap-1 mt-1 text-gray-500">
                                <Timer size={14} />
                                <span className="text-xs">{stepDuration}</span>
                              </div>
                            )
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Category order for "By Type" sorting (serving order)
const CATEGORY_ORDER = [
  { key: 'beverage', label: 'Beverages' },
  { key: 'appetizer', label: 'Appetizers' },
  { key: 'soup', label: 'Soups & Stews' },
  { key: 'salad', label: 'Salads' },
  { key: 'main_dish', label: 'Main Dishes' },
  { key: 'side', label: 'Sides' },
  { key: 'dessert', label: 'Desserts' },
  { key: 'breakfast', label: 'Breakfast & Brunch' },
  { key: 'other', label: 'Other' },
];

// Main ingredient order for "By Ingredient" sorting
const INGREDIENT_ORDER = [
  { key: 'beef', label: 'Beef' },
  { key: 'pork', label: 'Pork' },
  { key: 'poultry', label: 'Poultry' },
  { key: 'lamb_game', label: 'Lamb & Game' },
  { key: 'seafood', label: 'Seafood' },
  { key: 'beans_lentils', label: 'Beans & Lentils' },
  { key: 'tofu_soy', label: 'Tofu & Soy' },
  { key: 'vegetables', label: 'Vegetables' },
  { key: 'grains_pasta_starches', label: 'Grains, Pasta & Starches' },
  { key: 'eggs_dairy', label: 'Eggs & Dairy' },
  { key: 'fruits', label: 'Fruits' },
  { key: 'sweets_baking', label: 'Sweets & Baking' },
  { key: 'drinks', label: 'Drinks' },
  { key: 'other', label: 'Other' },
];

// Main appliance order for "By Appliance" sorting
const APPLIANCE_ORDER = [
  { key: 'oven', label: 'Oven' },
  { key: 'stovetop', label: 'Stovetop' },
  { key: 'air_fryer', label: 'Air Fryer' },
  { key: 'pressure_cooker', label: 'Pressure Cooker' },
  { key: 'slow_cooker', label: 'Slow Cooker' },
  { key: 'grill', label: 'Grill' },
  { key: 'microwave', label: 'Microwave' },
  { key: 'countertop_oven', label: 'Countertop Oven' },
  { key: 'multicooker', label: 'Multicooker' },
  { key: 'blender', label: 'Blender' },
  { key: 'food_processor', label: 'Food Processor' },
  { key: 'mixer', label: 'Mixer' },
  { key: 'juicer', label: 'Juicer' },
  { key: 'ice_cream_machine', label: 'Ice Cream / Slush Machine' },
  { key: 'other_none', label: 'Other / None' },
];

// Time period labels for "Recently Viewed" sorting
const getTimePeriod = (lastViewed, createdAt, sortBy) => {
  // Use last_viewed when sorting by "Recently Viewed", otherwise use created_at
  // Fall back to created_at if last_viewed is not available
  let dateToUse;
  if (sortBy === 'last_viewed') {
    dateToUse = lastViewed || createdAt;
  } else {
    dateToUse = createdAt;
  }
  
  if (!dateToUse) return 'older';
  
  // Server sends dates without 'Z' suffix but they ARE in UTC
  // Append 'Z' to ensure JavaScript parses them as UTC
  let dateString = dateToUse;
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    dateString = dateString + 'Z';
  }
  
  const now = new Date();
  const recipeDate = new Date(dateString);
  
  // Get today's date at midnight in LOCAL timezone
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Get the recipe date at midnight in LOCAL timezone
  const recipeDay = new Date(recipeDate.getFullYear(), recipeDate.getMonth(), recipeDate.getDate());
  
  // Calculate difference in days
  const diffMs = today.getTime() - recipeDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return 'this_week';
  if (diffDays < 30) return 'this_month';
  if (diffDays < 90) return 'last_3_months';
  return 'older';
};

const TIME_PERIOD_ORDER = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_3_months', label: 'Last 3 Months' },
  { key: 'older', label: 'Older' },
];

// Helper to extract source name from recipe
const getRecipeSource = (recipe) => {
  const siteNames = {
    'allrecipes.com': 'Allrecipes',
    'foodnetwork.com': 'Food Network',
    'food52.com': 'Food52',
    'bonappetit.com': 'Bon Appétit',
    'seriouseats.com': 'Serious Eats',
    'epicurious.com': 'Epicurious',
    'nytimes.com': 'NYT Cooking',
    'cooking.nytimes.com': 'NYT Cooking',
    'tasty.co': 'Tasty',
    'delish.com': 'Delish',
    'simplyrecipes.com': 'Simply Recipes',
    'budgetbytes.com': 'Budget Bytes',
    'thekitchn.com': 'The Kitchn',
    'kingarthurbaking.com': 'King Arthur Baking',
    'smittenkitchen.com': 'Smitten Kitchen',
    'minimalistbaker.com': 'Minimalist Baker',
    'halfbakedharvest.com': 'Half Baked Harvest',
    'loveandlemons.com': 'Love and Lemons',
    'cookieandkate.com': 'Cookie and Kate',
    'pinchofyum.com': 'Pinch of Yum',
    'recipetineats.com': 'RecipeTin Eats',
    'cafedelites.com': 'Cafe Delites',
    'damndelicious.net': 'Damn Delicious',
    'skinnytaste.com': 'Skinnytaste',
    'wellplated.com': 'Well Plated',
    'hostthetoast.com': 'Host The Toast',
    'gimmesomeoven.com': 'Gimme Some Oven',
    'sallysbakingaddiction.com': 'Sally\'s Baking Addiction',
    'joyfoodsunshine.com': 'Joy Food Sunshine',
    'bettycrocker.com': 'Betty Crocker',
    'pillsbury.com': 'Pillsbury',
    'marthastewart.com': 'Martha Stewart',
    'tasteofhome.com': 'Taste of Home',
    'southernliving.com': 'Southern Living',
    'myrecipes.com': 'My Recipes',
    'eatingwell.com': 'EatingWell',
    'bbcgoodfood.com': 'BBC Good Food',
    'jamieoliver.com': 'Jamie Oliver',
    'nigella.com': 'Nigella',
    'thepioneerwoman.com': 'The Pioneer Woman',
    'ina-garten.com': 'Ina Garten',
    'barefootcontessa.com': 'Barefoot Contessa'
  };

  const friendlyDomain = (urlStr) => {
    try {
      const url = new URL(urlStr);
      let domain = url.hostname.replace('www.', '');
      return siteNames[domain] || domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
    } catch {
      return null;
    }
  };

  // Priority: source_url > book_source > custom_source > "Honey Cooking Companion"
  if (recipe.source_url) {
    return friendlyDomain(recipe.source_url) || 'Web Import';
  }
  
  if (recipe.book_source && recipe.book_source.title) {
    // Format: "Book Name" or "Book Name by Author"
    const book = recipe.book_source;
    return book.author ? `${book.title}` : book.title;
  }
  
  if (recipe.custom_source && recipe.custom_source.trim()) {
    const customSrc = recipe.custom_source.trim();
    // If custom_source looks like a URL, extract the friendly site name
    return friendlyDomain(customSrc) || customSrc;
  }
  
  // Default for recipes created in the app
  return 'Honey Cooking Companion';
};

const RecipeBox = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Helper to create a lightweight cache (without large image data)
  const createLightweightCache = (recipesData) => {
    return recipesData.map(r => ({
      recipe_id: r.recipe_id,
      title: r.title,
      description: r.description,
      category: r.category,
      current_meal: r.current_meal,
      up_next: r.up_next,  // Include up_next flag for instant load
      cooked: r.cooked,
      rating: r.rating,
      labels: r.labels,
      prep_time: r.prep_time,
      cook_time: r.cook_time,
      servings: r.servings,
      last_viewed: r.last_viewed,
      created_at: r.created_at,
      deleted: r.deleted,
      // Flag for lazy-loading thumbnails (must match RecipeCard's expected field name)
      has_image: r.has_image || (r.images && r.images.length > 0) || !!r.image_url,
      // Keep full ingredients and instructions for instant recipe detail loading
      ingredients: r.ingredients || [],
      instructions: r.instructions || [],
      recipe_tips: r.recipe_tips || [],
      notes: r.notes || '',
      // Source fields for "By Source" sorting
      source_url: r.source_url || null,
      custom_source: r.custom_source || null,
      book_source: r.book_source || null
    }));
  };
  
  // Cache version - increment when cache structure changes
  const CACHE_VERSION = 9; // Bumped to force cache clear due to user_id mismatch bug
  
  // Helper to clear all user-specific caches
  const clearUserCaches = () => {
    localStorage.removeItem('recipesCache');
    localStorage.removeItem('recipesCacheUserId');
    localStorage.removeItem('recipesCacheTime');
    localStorage.removeItem('userCache');
    localStorage.removeItem('currentMealOrder');
    localStorage.removeItem('upNextOrder');
    localStorage.removeItem('miseEnPlaceContent');
    localStorage.removeItem('miseEnPlaceCacheKey');
    localStorage.removeItem('miseCheckedItems');
  };
  
  // Check if cached data belongs to current user (prevents cross-user cache bugs)
  const isCacheValidForUser = (currentUserId) => {
    try {
      const cachedUserId = localStorage.getItem('recipesCacheUserId');
      // If no cached user ID, cache is invalid
      if (!cachedUserId) return false;
      // If cached user ID doesn't match current user, cache is invalid
      return cachedUserId === currentUserId;
    } catch {
      return false;
    }
  };
  
  // Initialize recipes from localStorage cache for instant load
  const [recipes, setRecipes] = useState(() => {
    try {
      const cacheVersion = localStorage.getItem('recipesCacheVersion');
      // Clear old cache if version mismatch
      if (cacheVersion !== String(CACHE_VERSION)) {
        clearUserCaches();
        localStorage.setItem('recipesCacheVersion', String(CACHE_VERSION));
        return [];
      }
      const cached = localStorage.getItem('recipesCache');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  
  // Initialize filteredRecipes from cache too for instant display
  const [filteredRecipes, setFilteredRecipes] = useState(() => {
    try {
      const cached = localStorage.getItem('recipesCache');
      const savedSortBy = localStorage.getItem('recipeSortBy') || 'last_viewed';
      if (cached) {
        const recipes = JSON.parse(cached);
        const currentMeal = recipes.filter(r => r.current_meal && !r.deleted);
        const upNext = recipes.filter(r => r.up_next && !r.current_meal && !r.deleted);
        let regular = recipes.filter(r => !r.current_meal && !r.up_next && !r.deleted);
        
        // Apply saved sort order to avoid visual jump on re-render
        if (savedSortBy === 'category') {
          const CATEGORY_ORDER_KEYS = ['beverage', 'appetizer', 'soup', 'salad', 'main_dish', 'side', 'dessert', 'breakfast', 'other'];
          const categoryIndex = (cat) => {
            const idx = CATEGORY_ORDER_KEYS.indexOf(cat);
            return idx === -1 ? CATEGORY_ORDER_KEYS.length : idx;
          };
          regular.sort((a, b) => {
            const catCompare = categoryIndex(a.category) - categoryIndex(b.category);
            if (catCompare !== 0) return catCompare;
            return (a.title || '').localeCompare(b.title || '');
          });
        } else if (savedSortBy === 'label') {
          regular.sort((a, b) => {
            const aLabel = (a.labels && a.labels[0]) || 'zzz';
            const bLabel = (b.labels && b.labels[0]) || 'zzz';
            const labelCompare = aLabel.localeCompare(bLabel);
            if (labelCompare !== 0) return labelCompare;
            return (a.title || '').localeCompare(b.title || '');
          });
        } else if (savedSortBy === 'source') {
          regular.sort((a, b) => {
            const aSource = getRecipeSource(a);
            const bSource = getRecipeSource(b);
            if (aSource === 'Honey Cooking Companion' && bSource !== 'Honey Cooking Companion') return -1;
            if (bSource === 'Honey Cooking Companion' && aSource !== 'Honey Cooking Companion') return 1;
            const sourceCompare = aSource.localeCompare(bSource);
            if (sourceCompare !== 0) return sourceCompare;
            return (a.title || '').localeCompare(b.title || '');
          });
        } else if (savedSortBy === 'title') {
          regular.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        }
        // Default (last_viewed) sort relies on data order from API
        
        return { currentMeal, upNext, regular };
      }
      return { currentMeal: [], upNext: [], regular: [] };
    } catch {
      return { currentMeal: [], upNext: [], regular: [] };
    }
  });
  
  // Only show loading if we don't have cached recipes
  const [loading, setLoading] = useState(() => {
    try {
      const cached = localStorage.getItem('recipesCache');
      return !cached || JSON.parse(cached).length === 0;
    } catch {
      return true;
    }
  });
  
  // Initialize search from URL params
  const [searchQuery, setSearchQuery] = useState(() => {
    return searchParams.get('q') || '';
  });
  const [isSearchFocused, setIsSearchFocused] = useState(false); // Track search focus for hiding bottom nav
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cookedFilter, setCookedFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const location = useLocation();
  // Initialize user from navigation state (from AuthCallback) or localStorage cache
  const [user, setUser] = useState(() => {
    // First priority: user passed from AuthCallback
    if (location.state?.user) {
      localStorage.setItem('userCache', JSON.stringify(location.state.user));
      return location.state.user;
    }
    // Fallback: cached user
    try {
      const cached = localStorage.getItem('userCache');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [measurementSystem, setMeasurementSystem] = useState(() => {
    return user?.measurement_system || 'imperial';
  });
  const userMenuRef = useRef(null);
  
  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedUpNextItem, setDraggedUpNextItem] = useState(null);
  
  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  
  // View, sort, and deleted filter state - restore from localStorage
  // Default to list view on mobile for better visibility
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('recipeBoxViewMode');
    if (saved) return saved;
    // Default to list on mobile (screen width < 768px)
    return window.innerWidth < 768 ? 'list' : 'grid';
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('recipeBoxSortBy') || 'last_viewed';
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  
  // Up Next reorder modal state
  const [showUpNextReorderModal, setShowUpNextReorderModal] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [draggedReorderItem, setDraggedReorderItem] = useState(null);
  
  // Currently Cooking reorder modal state
  const [showCurrentlyReorderModal, setShowCurrentlyReorderModal] = useState(false);
  const [currentlyReorderList, setCurrentlyReorderList] = useState([]);
  const [draggedCurrentlyReorderItem, setDraggedCurrentlyReorderItem] = useState(null);
  
  // Labels state
  const [userLabels, setUserLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(() => {
    return localStorage.getItem('recipeBoxLabelFilter') || 'all';
  });
  const [showManageLabelsModal, setShowManageLabelsModal] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [creatingLabel, setCreatingLabel] = useState(false);
  
  // Mise en Place state - persist to localStorage
  const [showMiseEnPlace, setShowMiseEnPlace] = useState(false);
  const [miseEnPlaceContent, setMiseEnPlaceContent] = useState(() => {
    return localStorage.getItem('miseEnPlaceContent') || '';
  });
  const [miseEnPlaceLoading, setMiseEnPlaceLoading] = useState(false);
  const [miseEnPlaceBackgroundLoading, setMiseEnPlaceBackgroundLoading] = useState(false);
  const miseEnPlaceCacheKeyRef = useRef(localStorage.getItem('miseEnPlaceCacheKey') || '');
  const [fullCurrentMealRecipes, setFullCurrentMealRecipes] = useState([]); // Full recipe data with ingredients for Mise en Place
  const [loadingFullRecipes, setLoadingFullRecipes] = useState(false); // Loading state for ingredient data
  
  // Print dropdown state
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const [showUpNextPrintDropdown, setShowUpNextPrintDropdown] = useState(false);
  
  // Collapsible sections ref for auto-scroll
  const groupedSectionsRef = useRef(null);

  // Reset collapsed sections when sort changes
  useEffect(() => {
    setCollapsedSections(new Set());
  }, [sortBy]);

  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const hasGroupedSections = ['category', 'last_viewed', 'label', 'source', 'ingredient', 'appliance'].includes(sortBy);

  const expandAllSections = () => {
    setCollapsedSections(new Set());
    setTimeout(() => {
      groupedSectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const collapseAllSections = (allKeys) => {
    setCollapsedSections(new Set(allKeys));
    setTimeout(() => {
      groupedSectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // New Recipe button tip state - shows 3 times then dismisses
  const [showNewRecipeTip, setShowNewRecipeTip] = useState(() => {
    try {
      const tipCount = parseInt(localStorage.getItem('newRecipeTipCount') || '0');
      // Show tip if seen less than 3 times and user has at least 1 recipe (not first visit)
      return tipCount < 3;
    } catch {
      return true;
    }
  });
  
  // First-time Currently Cooking tip state
  const [showCurrentlyCookingTip, setShowCurrentlyCookingTip] = useState(() => {
    return !localStorage.getItem('hasSeenCurrentlyCookingTip');
  });
  
  // First-time Up Next tip state
  const [showUpNextTip, setShowUpNextTip] = useState(() => {
    return !localStorage.getItem('hasSeenUpNextTip');
  });
  
  // First-time Up Next Shopping List tip state
  const [showUpNextShoppingTip, setShowUpNextShoppingTip] = useState(false);
  
  // Pulse shopping cart icon briefly when recipe added to Up Next
  const [pulseShoppingIcon, setPulseShoppingIcon] = useState(false);
  
  // Help tiles visibility state (from HowHoneyWorks component)
  const [showCookingHelpTile, setShowCookingHelpTile] = useState(() => {
    return !localStorage.getItem('dismissedCookingHelpTile');
  });
  const [showUpNextHelpTile, setShowUpNextHelpTile] = useState(() => {
    return !localStorage.getItem('dismissedUpNextHelpTile');
  });
  
  // Listen for event to show help tiles when tutorial is opened
  useEffect(() => {
    const handleShowHelpTiles = () => {
      setShowCookingHelpTile(true);
      setShowUpNextHelpTile(true);
      // Clear the dismissed flags so they show again
      localStorage.removeItem('dismissedCookingHelpTile');
      localStorage.removeItem('dismissedUpNextHelpTile');
      // Also reset the recipe detail modal so it shows when user opens a recipe
      localStorage.removeItem('hasSeenCookButtonTipV2');
    };
    
    // When help is closed, hide New Recipe tip so it doesn't overlap
    const handleHideNewRecipeTip = () => {
      setShowNewRecipeTip(false);
    };
    
    // Scroll to top on mobile or to Currently Cooking on desktop when help is closed
    const handleScrollToTop = (event) => {
      const { isMobile } = event.detail || {};
      if (isMobile) {
        // Scroll to very top on mobile
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // On desktop, scroll so Currently Cooking section is at top
        const cookingSection = document.querySelector('[data-section="currently-cooking"]');
        if (cookingSection) {
          cookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };
    
    window.addEventListener(SHOW_HELP_TILES_EVENT, handleShowHelpTiles);
    window.addEventListener(HIDE_NEW_RECIPE_TIP_EVENT, handleHideNewRecipeTip);
    window.addEventListener(SCROLL_TO_TOP_EVENT, handleScrollToTop);
    return () => {
      window.removeEventListener(SHOW_HELP_TILES_EVENT, handleShowHelpTiles);
      window.removeEventListener(HIDE_NEW_RECIPE_TIP_EVENT, handleHideNewRecipeTip);
      window.removeEventListener(SCROLL_TO_TOP_EVENT, handleScrollToTop);
    };
  }, []);
  
  // Handlers for dismissing help tiles
  const handleDismissCookingHelpTile = () => {
    setShowCookingHelpTile(false);
    localStorage.setItem('dismissedCookingHelpTile', 'true');
  };
  
  const handleDismissUpNextHelpTile = () => {
    setShowUpNextHelpTile(false);
    localStorage.setItem('dismissedUpNextHelpTile', 'true');
  };
  
  const miseScrollRef = useRef(null);
  const [miseCheckedItems, setMiseCheckedItems] = useState(() => {
    try {
      const saved = localStorage.getItem('miseCheckedItems');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  // Track if initial data load has completed to avoid regenerating cached mise en place
  const initialLoadCompleteRef = useRef(false);

  // Persist Mise en Place content to localStorage
  // Reset scroll position on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to Up Next section when arriving from RecipeDetail after adding to Up Next
  useEffect(() => {
    if (location.state?.scrollToUpNext) {
      // Small delay to ensure the section is rendered
      setTimeout(() => {
        const upNextSection = document.querySelector('[data-section="up-next"]');
        if (upNextSection) {
          upNextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        // Show Up Next Shopping tip if user hasn't seen it yet OR if explicitly requested (from Groceries banner)
        if (!localStorage.getItem('hasSeenUpNextShoppingTip') || location.state?.showShoppingTip) {
          setShowUpNextShoppingTip(true);
        } else {
          // If they've seen the tip, just pulse the icon briefly
          setPulseShoppingIcon(true);
          setTimeout(() => setPulseShoppingIcon(false), 3000);
        }
      }, 100);
      // Clear the state so it doesn't scroll again on re-render
      window.history.replaceState({}, '', '/recipe-box');
    }
  }, [location.state]);

  // Show welcome toast for early adopters (only once)
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenEarlyAdopterWelcome');
    if (!hasSeenWelcome) {
      // Delay slightly so it appears after page loads
      const timer = setTimeout(() => {
        toast('🍯 Welcome! You\'ve been enrolled in the Friends & Family free trial of Honey! I welcome all feedback and truly appreciate your willingness to try something new! — Rachel', {
          duration: 8000,
        });
        localStorage.setItem('hasSeenEarlyAdopterWelcome', 'true');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('miseEnPlaceContent', miseEnPlaceContent);
  }, [miseEnPlaceContent]);

  // Persist Mise en Place cache key to localStorage
  useEffect(() => {
    localStorage.setItem('miseEnPlaceCacheKey', miseEnPlaceCacheKeyRef.current);
  }, [miseEnPlaceContent]); // Update when content changes (which is when cache key changes)

  // Persist Mise en Place checked items to localStorage
  useEffect(() => {
    localStorage.setItem('miseCheckedItems', JSON.stringify(miseCheckedItems));
  }, [miseCheckedItems]);

  // Fetch full recipe data (with ingredients) when Mise en Place is opened or current meal changes
  useEffect(() => {
    const fetchFullRecipes = async () => {
      if (!showMiseEnPlace || !filteredRecipes.currentMeal || filteredRecipes.currentMeal.length === 0) {
        return;
      }
      
      const recipeIds = filteredRecipes.currentMeal.map(r => r.recipe_id);
      
      // Check if we already have full data for these recipes
      const existingIds = fullCurrentMealRecipes.map(r => r.recipe_id);
      const needsFetch = recipeIds.some(id => !existingIds.includes(id)) || 
                         existingIds.some(id => !recipeIds.includes(id));
      
      if (!needsFetch && fullCurrentMealRecipes.length > 0) {
        return; // Already have full data for current recipes
      }
      
      setLoadingFullRecipes(true);
      try {
        const fullRecipes = await Promise.all(
          recipeIds.map(id => 
            recipeApi.getById(id).then(res => res.data).catch(() => null)
          )
        );
        const validRecipes = fullRecipes.filter(r => r !== null);
        
        // Preserve the order from filteredRecipes.currentMeal
        const orderedRecipes = recipeIds
          .map(id => validRecipes.find(r => r.recipe_id === id))
          .filter(r => r !== null);
        
        setFullCurrentMealRecipes(orderedRecipes);
      } catch (error) {
        console.error('Failed to fetch full recipe data for Mise en Place:', error);
      } finally {
        setLoadingFullRecipes(false);
      }
    };
    
    fetchFullRecipes();
  }, [showMiseEnPlace, filteredRecipes.currentMeal]);

  // Get saved order for current meal from localStorage
  const getCurrentMealOrder = () => {
    try {
      const saved = localStorage.getItem('currentMealOrder');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  };

  // Save order to localStorage
  const saveCurrentMealOrder = (recipeIds) => {
    localStorage.setItem('currentMealOrder', JSON.stringify(recipeIds));
  };
  
  // Get Up Next order from localStorage
  const getUpNextOrder = () => {
    try {
      const saved = localStorage.getItem('upNextOrder');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  };

  // Save Up Next order to localStorage
  const saveUpNextOrder = (recipeIds) => {
    localStorage.setItem('upNextOrder', JSON.stringify(recipeIds));
  };
  
  // Persist view preferences to localStorage
  useEffect(() => {
    localStorage.setItem('recipeBoxViewMode', viewMode);
  }, [viewMode]);
  
  useEffect(() => {
    localStorage.setItem('recipeBoxSortBy', sortBy);
  }, [sortBy]);
  
  useEffect(() => {
    localStorage.setItem('recipeBoxLabelFilter', selectedLabel);
  }, [selectedLabel]);

  // Sync search query to URL params
  useEffect(() => {
    if (searchQuery) {
      setSearchParams({ q: searchQuery }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [searchQuery, setSearchParams]);

  // Re-sync from localStorage cache when page becomes visible (e.g., navigating back from RecipeDetail)
  // NOTE: We intentionally do NOT listen to 'focus' events - they fire too frequently during normal
  // page interactions (clicking buttons, etc.) and cause race conditions with optimistic UI updates.
  // The 'visibilitychange' event is sufficient for the "navigate away and come back" use case.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        try {
          const cached = localStorage.getItem('recipesCache');
          if (cached) {
            const cachedRecipes = JSON.parse(cached);
            // Force a new array reference to trigger re-render
            setRecipes([...cachedRecipes]);
          } else {
            // No cache - fetch fresh data (cache was invalidated, e.g., after creating a new recipe)
            fetchUserAndRecipes();
          }
        } catch (e) {
          // On error, fetch fresh
          fetchUserAndRecipes();
        }
      }
    };
    
    // Close menus when clicking outside
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="create-recipe-button"]') && !e.target.closest('.create-menu')) {
        setShowCreateMenu(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show New Recipe tip after user has recipes (not on first visit) and track view count
  useEffect(() => {
    if (recipes.length > 0 && showNewRecipeTip) {
      // Increment tip view count
      const tipCount = parseInt(localStorage.getItem('newRecipeTipCount') || '0');
      const newCount = tipCount + 1;
      localStorage.setItem('newRecipeTipCount', String(newCount));
      
      // Auto-dismiss after 3 views
      if (newCount >= 3) {
        // Dismiss tip after a short delay on the 3rd view
        setTimeout(() => setShowNewRecipeTip(false), 5000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipes.length]); // Only run when recipes count changes

  // Fetch fresh data from API
  // Use a ref to track the latest fetch to prevent stale responses from overwriting newer data
  const fetchIdRef = useRef(0);
  
  useEffect(() => {
    const fetchWithMerge = async () => {
      const thisFetchId = ++fetchIdRef.current;
      try {
        const [userRes, recipesRes, labelsRes] = await Promise.all([
          auth.getCurrentUser(),
          recipeApi.getAll({ include_deleted: showDeleted }),
          labelsApi.getAll()
        ]);
        
        // If a newer fetch has started, discard this stale response
        if (thisFetchId !== fetchIdRef.current) {
          return;
        }
        
        setUser(userRes.data);
        // Sync measurement system from server
        if (userRes.data?.measurement_system) {
          setMeasurementSystem(userRes.data.measurement_system);
        }
        // Cache user data to prevent flicker on navigation
        try {
          localStorage.setItem('userCache', JSON.stringify(userRes.data));
        } catch (e) {
          // Ignore cache errors
        }
        
        // Use API data as source of truth
        let finalRecipes = recipesRes.data;
        
        setRecipes(finalRecipes);
        setUserLabels(labelsRes.data.labels || []);
        
        // Update cache with fresh API data
        try {
          localStorage.setItem('recipesCache', JSON.stringify(createLightweightCache(finalRecipes)));
          localStorage.setItem('recipesCacheTime', String(Date.now()));
        } catch (e) {
          console.warn('Failed to cache recipes:', e);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        // On error, try to use cache
        const cached = localStorage.getItem('recipesCache');
        if (cached) {
          try {
            setRecipes(JSON.parse(cached));
          } catch (e) {
            // Ignore
          }
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchWithMerge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  useEffect(() => {
    applyFilters();
  }, [recipes, searchQuery, selectedCategory, cookedFilter, sortBy, selectedLabel]);

  // Helper to clear Mise en Place cache (localStorage + state)
  const clearMiseEnPlaceCache = () => {
    setMiseEnPlaceContent('');
    setMiseCheckedItems({});
    setFullCurrentMealRecipes([]); // Also clear full recipe data
    miseEnPlaceCacheKeyRef.current = '';
    localStorage.removeItem('miseEnPlaceContent');
    localStorage.removeItem('miseEnPlaceCacheKey');
    localStorage.removeItem('miseCheckedItems');
  };

  // Sync Shopping List cache key in background when Currently Cooking changes
  // This allows ShoppingListPage to know instantly if recipes changed
  useEffect(() => {
    if (!filteredRecipes.currentMeal) return;
    
    const recipeIds = filteredRecipes.currentMeal.map(r => r.recipe_id);
    const newCacheKey = [...recipeIds].sort().join(',');
    const storedCacheKey = localStorage.getItem('shoppingListCacheKey') || '';
    
    // If recipes changed, update cache key and clear cached data
    if (newCacheKey !== storedCacheKey) {
      localStorage.setItem('shoppingListCacheKey', newCacheKey);
      localStorage.removeItem('shoppingListChecked');
    }
  }, [filteredRecipes.currentMeal]);

  // Auto-generate Mise en Place in background when Currently Cooking changes
  // TEMPORARILY DISABLED - causing server blocking issues
  // Uses delay + AbortController to prioritize real-time user interactions
  const miseAbortControllerRef = useRef(null);
  const miseGenerationInProgressRef = useRef(false);
  const lastGeneratedCacheKeyRef = useRef('');
  
  useEffect(() => {
    // DISABLED: Background mise en place generation is temporarily disabled
    // to prevent server blocking. User can still generate manually via the button.
    return;
    
    // Cancel any pending mise en place generation
    if (miseAbortControllerRef.current) {
      miseAbortControllerRef.current.abort();
      miseAbortControllerRef.current = null;
    }
    
    const generateInBackground = async () => {
      if (!filteredRecipes.currentMeal || filteredRecipes.currentMeal.length < 1) {
        // Only clear cache if we've done at least one full load cycle
        // This prevents clearing on initial empty state
        if (initialLoadCompleteRef.current) {
          clearMiseEnPlaceCache();
        }
        return;
      }
      
      const recipeIds = filteredRecipes.currentMeal.map(r => r.recipe_id);
      const cacheKey = [...recipeIds].sort().join(',');
      
      // DEDUPLICATION: Skip if we already generated or are generating this exact combination
      if (cacheKey === lastGeneratedCacheKeyRef.current) {
        return;
      }
      
      // Skip if already generating
      if (miseGenerationInProgressRef.current) {
        return;
      }
      
      // Skip if we already have this cached (check both ref and localStorage)
      const storedCacheKey = localStorage.getItem('miseEnPlaceCacheKey') || '';
      if (cacheKey === storedCacheKey && miseEnPlaceContent) {
        // Make sure ref is in sync
        miseEnPlaceCacheKeyRef.current = cacheKey;
        lastGeneratedCacheKeyRef.current = cacheKey;
        initialLoadCompleteRef.current = true;
        return;
      }
      
      // On initial load, if we have cached content that matches, don't regenerate
      if (!initialLoadCompleteRef.current && miseEnPlaceContent && storedCacheKey === cacheKey) {
        miseEnPlaceCacheKeyRef.current = cacheKey;
        lastGeneratedCacheKeyRef.current = cacheKey;
        initialLoadCompleteRef.current = true;
        return;
      }
      
      // Mark initial load as complete
      initialLoadCompleteRef.current = true;
      
      // Clear old checked items when recipes change
      setMiseCheckedItems({});
      localStorage.removeItem('miseCheckedItems');
      
      // Mark this cache key as being generated - do NOT reset on error to prevent loops
      lastGeneratedCacheKeyRef.current = cacheKey;
      miseGenerationInProgressRef.current = true;
      
      // DELAY: Wait 2 seconds before starting background generation
      // This gives the user time to navigate to a recipe without blocking
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Check if we were canceled during the delay
      if (miseAbortControllerRef.current?.signal?.aborted) {
        miseGenerationInProgressRef.current = false;
        return;
      }
      
      // Create AbortController for this request
      const abortController = new AbortController();
      miseAbortControllerRef.current = abortController;
      
      setMiseEnPlaceBackgroundLoading(true);
      
      try {
        const response = await ai.generateMiseEnPlace(recipeIds, abortController.signal);
        // Only update if not aborted
        if (!abortController.signal.aborted) {
          setMiseEnPlaceContent(response.data.timeline);
          miseEnPlaceCacheKeyRef.current = cacheKey;
          localStorage.setItem('miseEnPlaceCacheKey', cacheKey);
        }
      } catch (error) {
        // Don't log abort errors - they're expected when user navigates
        if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
          console.error('Background mise en place generation failed:', error);
        }
        // Do NOT reset cache key on error - this prevents infinite retry loops
        // User can manually trigger regeneration if needed
      } finally {
        setMiseEnPlaceBackgroundLoading(false);
        miseGenerationInProgressRef.current = false;
        if (miseAbortControllerRef.current === abortController) {
          miseAbortControllerRef.current = null;
        }
      }
    };
    
    generateInBackground();
    
    // Cleanup: cancel pending request when component unmounts or deps change
    return () => {
      if (miseAbortControllerRef.current) {
        miseAbortControllerRef.current.abort();
        miseAbortControllerRef.current = null;
      }
    };
  // Use recipe IDs as dependency instead of object reference to prevent unnecessary re-runs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRecipes.currentMeal?.map(r => r.recipe_id).join(',')]);

  const fetchUserAndRecipes = async () => {
    try {
      // First, get current user to validate cache
      const userRes = await auth.getCurrentUser();
      const currentUser = userRes.data;
      
      // CRITICAL: Check if cached data belongs to this user
      // If not, clear all caches to prevent cross-user data leakage
      if (!isCacheValidForUser(currentUser.user_id)) {
        console.log('Cache user mismatch - clearing all user caches');
        clearUserCaches();
        // Clear local state too
        setRecipes([]);
        setFilteredRecipes({ currentMeal: [], upNext: [], regular: [] });
      }
      
      // Now fetch recipes and labels
      const [recipesRes, labelsRes] = await Promise.all([
        recipeApi.getAll({ include_deleted: showDeleted }),
        labelsApi.getAll()
      ]);
      
      setUser(currentUser);
      // Cache user data to prevent flicker on navigation
      try {
        localStorage.setItem('userCache', JSON.stringify(currentUser));
      } catch (e) {
        // Ignore cache errors
      }
      setRecipes(recipesRes.data);
      setUserLabels(labelsRes.data.labels || []);
      
      // Cache lightweight version for instant load on return
      // IMPORTANT: Also store the user_id to validate cache on next load
      try {
        localStorage.setItem('recipesCache', JSON.stringify(createLightweightCache(recipesRes.data)));
        localStorage.setItem('recipesCacheUserId', currentUser.user_id);
        localStorage.setItem('recipesCacheTime', String(Date.now()));
      } catch (e) {
        console.warn('Failed to cache recipes:', e);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...recipes];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(recipe =>
        recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (recipe.ingredients && recipe.ingredients.some(ing => ing.name?.toLowerCase().includes(searchQuery.toLowerCase())))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(recipe => recipe.category === selectedCategory);
    }

    // Cooked filter
    if (cookedFilter === 'cooked') {
      filtered = filtered.filter(recipe => recipe.cooked);
    } else if (cookedFilter === 'not_cooked') {
      filtered = filtered.filter(recipe => !recipe.cooked);
    }
    
    // Label filter
    if (selectedLabel !== 'all') {
      filtered = filtered.filter(recipe => 
        recipe.labels && recipe.labels.includes(selectedLabel)
      );
    }

    // Separate current meal recipes (they'll be shown at top)
    const currentMealRecipes = filtered.filter(r => r.current_meal);
    // Up Next recipes - have the up_next flag but not in Currently Cooking
    const upNextRecipes = filtered.filter(r => r.up_next && !r.current_meal);
    // Regular recipes - not in Currently Cooking and not in Up Next
    const regularRecipes = filtered.filter(r => !r.current_meal && !r.up_next);

    // Apply sorting to regular recipes only
    if (sortBy === 'title') {
      regularRecipes.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'last_viewed') {
      regularRecipes.sort((a, b) => {
        // Use last_viewed if available, otherwise fall back to created_at
        const aDate = a.last_viewed || a.created_at;
        const bDate = b.last_viewed || b.created_at;
        const aViewed = aDate ? new Date(aDate) : new Date(0);
        const bViewed = bDate ? new Date(bDate) : new Date(0);
        return bViewed - aViewed;
      });
    } else if (sortBy === 'category') {
      // Sort by category order, then alphabetically by title within category
      const categoryIndex = (cat) => {
        const idx = CATEGORY_ORDER.findIndex(c => c.key === cat);
        return idx === -1 ? CATEGORY_ORDER.length : idx;
      };
      regularRecipes.sort((a, b) => {
        const catCompare = categoryIndex(a.category) - categoryIndex(b.category);
        if (catCompare !== 0) return catCompare;
        // Within same category, sort alphabetically by title
        return (a.title || '').localeCompare(b.title || '');
      });
    } else if (sortBy === 'label') {
      // Sort by label, then alphabetically by title within each label
      regularRecipes.sort((a, b) => {
        const aLabel = (a.labels && a.labels[0]) || 'zzz';
        const bLabel = (b.labels && b.labels[0]) || 'zzz';
        const labelCompare = aLabel.localeCompare(bLabel);
        if (labelCompare !== 0) return labelCompare;
        // Within same label, sort alphabetically by title
        return (a.title || '').localeCompare(b.title || '');
      });
    } else if (sortBy === 'source') {
      // Sort by source, then alphabetically by title within each source
      regularRecipes.sort((a, b) => {
        const aSource = getRecipeSource(a);
        const bSource = getRecipeSource(b);
        // "Honey Cooking Companion" comes first
        if (aSource === 'Honey Cooking Companion' && bSource !== 'Honey Cooking Companion') return -1;
        if (bSource === 'Honey Cooking Companion' && aSource !== 'Honey Cooking Companion') return 1;
        const sourceCompare = aSource.localeCompare(bSource);
        if (sourceCompare !== 0) return sourceCompare;
        // Within same source, sort alphabetically by title
        return (a.title || '').localeCompare(b.title || '');
      });
    } else if (sortBy === 'ingredient') {
      // Sort by main ingredient order, then alphabetically
      const ingredientIndex = (ing) => {
        const idx = INGREDIENT_ORDER.findIndex(i => i.key === ing);
        return idx === -1 ? INGREDIENT_ORDER.length : idx;
      };
      regularRecipes.sort((a, b) => {
        const aIng = (a.main_ingredients && a.main_ingredients[0]) || 'zzz';
        const bIng = (b.main_ingredients && b.main_ingredients[0]) || 'zzz';
        const ingCompare = ingredientIndex(aIng) - ingredientIndex(bIng);
        if (ingCompare !== 0) return ingCompare;
        return (a.title || '').localeCompare(b.title || '');
      });
    } else if (sortBy === 'appliance') {
      // Sort by main appliance order, then alphabetically
      const applianceIndex = (app) => {
        const idx = APPLIANCE_ORDER.findIndex(a => a.key === app);
        return idx === -1 ? APPLIANCE_ORDER.length : idx;
      };
      regularRecipes.sort((a, b) => {
        const aApp = (a.main_appliances && a.main_appliances[0]) || 'zzz';
        const bApp = (b.main_appliances && b.main_appliances[0]) || 'zzz';
        const appCompare = applianceIndex(aApp) - applianceIndex(bApp);
        if (appCompare !== 0) return appCompare;
        return (a.title || '').localeCompare(b.title || '');
      });
    }

    // Sort current meal recipes by saved order
    const savedOrder = getCurrentMealOrder();
    if (savedOrder.length > 0) {
      currentMealRecipes.sort((a, b) => {
        const aIndex = savedOrder.indexOf(a.recipe_id);
        const bIndex = savedOrder.indexOf(b.recipe_id);
        // Items not in saved order go to the end
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }

    // Sort Up Next recipes by saved order
    const savedUpNextOrder = getUpNextOrder();
    if (savedUpNextOrder.length > 0) {
      upNextRecipes.sort((a, b) => {
        const aIndex = savedUpNextOrder.indexOf(a.recipe_id);
        const bIndex = savedUpNextOrder.indexOf(b.recipe_id);
        // Items not in saved order go to the end
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }

    // Store all for rendering
    setFilteredRecipes({ currentMeal: currentMealRecipes, upNext: upNextRecipes, regular: regularRecipes });
  };

  // Helper to update lightweight cache for current_meal changes
  const updateCacheCurrentMeal = (recipeId, currentMealStatus) => {
    try {
      const cached = localStorage.getItem('recipesCache');
      if (cached) {
        const cachedRecipes = JSON.parse(cached);
        const updated = cachedRecipes.map(r => 
          r.recipe_id === recipeId ? { ...r, current_meal: currentMealStatus } : r
        );
        localStorage.setItem('recipesCache', JSON.stringify(updated));
        localStorage.setItem('recipesCacheTime', String(Date.now()));
      }
    } catch (e) {
      console.warn('Failed to update cache:', e);
    }
  };

  // Helper to clear all current_meal in cache
  const clearCacheCurrentMeal = () => {
    try {
      const cached = localStorage.getItem('recipesCache');
      if (cached) {
        const cachedRecipes = JSON.parse(cached);
        const updated = cachedRecipes.map(r => ({ ...r, current_meal: false }));
        localStorage.setItem('recipesCache', JSON.stringify(updated));
        localStorage.setItem('recipesCacheTime', String(Date.now()));
      }
    } catch (e) {
      console.warn('Failed to update cache:', e);
    }
  };

  // Handle toggling current meal status - optimistic update
  const handleToggleCurrentMeal = async (recipeId) => {
    // Find the recipe to determine current state
    const recipe = recipes.find(r => r.recipe_id === recipeId);
    const wasCurrentMeal = recipe?.current_meal;
    const newStatus = !wasCurrentMeal;
    
    // Track analytics
    if (newStatus) {
      trackCurrentlyCookingAdded(recipeId);
    } else {
      trackCurrentlyCookingRemoved(recipeId);
    }
    
    // Compute the updated recipes list
    const updatedRecipes = recipes.map(r => 
      r.recipe_id === recipeId ? { ...r, current_meal: newStatus } : r
    );
    
    // Optimistic update UI - update BOTH states for instant feedback
    setRecipes(updatedRecipes);
    
    // Also update filteredRecipes immediately (don't wait for useEffect)
    setFilteredRecipes(prev => {
      if (newStatus) {
        // Adding to Currently Cooking
        const recipeToMove = updatedRecipes.find(r => r.recipe_id === recipeId);
        return {
          currentMeal: [...prev.currentMeal, recipeToMove],
          regular: prev.regular.filter(r => r.recipe_id !== recipeId)
        };
      } else {
        // Removing from Currently Cooking
        const recipeToMove = updatedRecipes.find(r => r.recipe_id === recipeId);
        return {
          currentMeal: prev.currentMeal.filter(r => r.recipe_id !== recipeId),
          regular: [recipeToMove, ...prev.regular]
        };
      }
    });
    
    // Update localStorage recipe cache
    updateCacheCurrentMeal(recipeId, newStatus);
    
    // Clear mise en place cache when Currently Cooking changes
    clearMiseEnPlaceCache();
    
    // Make API call with explicit value (idempotent)
    try {
      await recipeApi.toggleCurrentMeal(recipeId, newStatus);
    } catch (error) {
      console.error('Failed to toggle current meal:', error);
      // Revert on error - refetch
      fetchUserAndRecipes();
    }
  };

  const [clearingCurrentMeal, setClearingCurrentMeal] = useState(false);
  
  // Clear all recipes from Currently Cooking
  const handleClearCurrentlyKitchen = async () => {
    if (!filteredRecipes.currentMeal || filteredRecipes.currentMeal.length === 0) return;
    
    const recipesToClear = filteredRecipes.currentMeal;
    
    setClearingCurrentMeal(true);
    
    // Optimistic update - immediately update UI
    setRecipes(prev => prev.map(r => 
      r.current_meal ? { ...r, current_meal: false } : r
    ));
    
    // Update cache
    clearCacheCurrentMeal();
    
    // Clear mise en place cache
    clearMiseEnPlaceCache();
    
    // Make API calls with explicit false value (idempotent)
    try {
      await Promise.all(
        recipesToClear.map(recipe => 
          recipeApi.toggleCurrentMeal(recipe.recipe_id, false)
        )
      );
    } catch (error) {
      console.error('Failed to clear currently cooking:', error);
      // Revert on error - refetch
      fetchUserAndRecipes();
    } finally {
      setClearingCurrentMeal(false);
    }
  };

  // Drag and drop handlers for Currently Cooking section
  const handleDragStart = (e, recipeId) => {
    setDraggedItem(recipeId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetRecipeId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetRecipeId) {
      setDraggedItem(null);
      return;
    }

    // Reorder the current meal recipes
    const currentOrder = filteredRecipes.currentMeal.map(r => r.recipe_id);
    const draggedIndex = currentOrder.indexOf(draggedItem);
    const targetIndex = currentOrder.indexOf(targetRecipeId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged item and insert at target position
      currentOrder.splice(draggedIndex, 1);
      currentOrder.splice(targetIndex, 0, draggedItem);
      
      // Save new order
      saveCurrentMealOrder(currentOrder);
      
      // Update filtered recipes to reflect new order
      const reorderedMeals = currentOrder
        .map(id => filteredRecipes.currentMeal.find(r => r.recipe_id === id))
        .filter(Boolean);
      
      setFilteredRecipes(prev => ({ ...prev, currentMeal: reorderedMeals }));
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Drag and drop handlers for Up Next section
  const handleUpNextDragStart = (e, recipeId) => {
    setDraggedUpNextItem(recipeId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleUpNextDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleUpNextDrop = (e, targetRecipeId) => {
    e.preventDefault();
    if (!draggedUpNextItem || draggedUpNextItem === targetRecipeId) {
      setDraggedUpNextItem(null);
      return;
    }

    // Reorder the up next recipes
    const currentOrder = filteredRecipes.upNext.map(r => r.recipe_id);
    const draggedIndex = currentOrder.indexOf(draggedUpNextItem);
    const targetIndex = currentOrder.indexOf(targetRecipeId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged item and insert at target position
      currentOrder.splice(draggedIndex, 1);
      currentOrder.splice(targetIndex, 0, draggedUpNextItem);
      
      // Save new order
      saveUpNextOrder(currentOrder);
      
      // Update filtered recipes to reflect new order
      const reorderedUpNext = currentOrder
        .map(id => filteredRecipes.upNext.find(r => r.recipe_id === id))
        .filter(Boolean);
      
      setFilteredRecipes(prev => ({ ...prev, upNext: reorderedUpNext }));
    }

    setDraggedUpNextItem(null);
  };

  const handleUpNextDragEnd = () => {
    setDraggedUpNextItem(null);
  };

  // Up Next Reorder Modal handlers
  const openUpNextReorderModal = () => {
    setReorderList([...filteredRecipes.upNext]);
    setShowUpNextReorderModal(true);
  };

  const handleReorderDragStart = (e, index) => {
    setDraggedReorderItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleReorderDragOver = (e, index) => {
    e.preventDefault();
    if (draggedReorderItem === null || draggedReorderItem === index) return;
    
    // Reorder the list
    const newList = [...reorderList];
    const draggedItem = newList[draggedReorderItem];
    newList.splice(draggedReorderItem, 1);
    newList.splice(index, 0, draggedItem);
    setReorderList(newList);
    setDraggedReorderItem(index);
  };

  const handleReorderDragEnd = () => {
    setDraggedReorderItem(null);
  };

  const saveReorderAndClose = () => {
    // Save the new order
    const newOrder = reorderList.map(r => r.recipe_id);
    saveUpNextOrder(newOrder);
    setFilteredRecipes(prev => ({ ...prev, upNext: reorderList }));
    setShowUpNextReorderModal(false);
  };

  // Touch-based reorder for mobile
  const moveItemUp = (index) => {
    if (index === 0) return;
    const newList = [...reorderList];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setReorderList(newList);
  };

  const moveItemDown = (index) => {
    if (index === reorderList.length - 1) return;
    const newList = [...reorderList];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setReorderList(newList);
  };

  // Currently Cooking Reorder Modal handlers
  const openCurrentlyReorderModal = () => {
    setCurrentlyReorderList([...filteredRecipes.currentMeal]);
    setShowCurrentlyReorderModal(true);
  };

  const handleCurrentlyReorderDragStart = (e, index) => {
    setDraggedCurrentlyReorderItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCurrentlyReorderDragOver = (e, index) => {
    e.preventDefault();
    if (draggedCurrentlyReorderItem === null || draggedCurrentlyReorderItem === index) return;
    
    const newList = [...currentlyReorderList];
    const draggedItem = newList[draggedCurrentlyReorderItem];
    newList.splice(draggedCurrentlyReorderItem, 1);
    newList.splice(index, 0, draggedItem);
    setCurrentlyReorderList(newList);
    setDraggedCurrentlyReorderItem(index);
  };

  const handleCurrentlyReorderDragEnd = () => {
    setDraggedCurrentlyReorderItem(null);
  };

  const saveCurrentlyReorderAndClose = () => {
    const newOrder = currentlyReorderList.map(r => r.recipe_id);
    saveCurrentMealOrder(newOrder);
    setFilteredRecipes(prev => ({ ...prev, currentMeal: currentlyReorderList }));
    setShowCurrentlyReorderModal(false);
  };

  const moveCurrentlyItemUp = (index) => {
    if (index === 0) return;
    const newList = [...currentlyReorderList];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setCurrentlyReorderList(newList);
  };

  const moveCurrentlyItemDown = (index) => {
    if (index === currentlyReorderList.length - 1) return;
    const newList = [...currentlyReorderList];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setCurrentlyReorderList(newList);
  };

  // Group recipes by category for "By Type" view
  const getGroupedByCategory = () => {
    const groups = {};
    filteredRecipes.regular.forEach(recipe => {
      const cat = recipe.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(recipe);
    });
    
    // Return in serving order
    return CATEGORY_ORDER
      .filter(c => groups[c.key] && groups[c.key].length > 0)
      .map(c => ({ ...c, recipes: groups[c.key] }));
  };

  // Group recipes by time period for "Recently Viewed"
  const getGroupedByTimePeriod = () => {
    const groups = {};
    filteredRecipes.regular.forEach(recipe => {
      const period = getTimePeriod(recipe.last_viewed, recipe.created_at, sortBy);
      if (!groups[period]) groups[period] = [];
      groups[period].push(recipe);
    });
    
    return TIME_PERIOD_ORDER
      .filter(p => groups[p.key] && groups[p.key].length > 0)
      .map(p => ({ ...p, recipes: groups[p.key] }));
  };

  // Group recipes by label for "By Label" view
  const getGroupedByLabel = () => {
    const groups = {};
    const unlabeledRecipes = [];
    
    filteredRecipes.regular.forEach(recipe => {
      if (!recipe.labels || recipe.labels.length === 0) {
        unlabeledRecipes.push(recipe);
      } else {
        // Recipe appears under EACH label it has
        recipe.labels.forEach(labelName => {
          if (!groups[labelName]) groups[labelName] = [];
          groups[labelName].push(recipe);
        });
      }
    });
    
    // Build result ordered by userLabels
    const result = userLabels
      .filter(label => groups[label.name] && groups[label.name].length > 0)
      .map(label => ({
        key: label.name,
        label: label.name,
        icon: label.icon,
        color: label.color,
        recipes: groups[label.name]
      }));
    
    // Add unlabeled at the end
    if (unlabeledRecipes.length > 0) {
      result.push({
        key: '_unlabeled',
        label: 'Unlabeled',
        icon: null,
        color: '#6b7280',
        recipes: unlabeledRecipes
      });
    }
    
    return result;
  };

  // Group recipes by source for "By Source" view
  const getGroupedBySource = () => {
    const groups = {};
    
    filteredRecipes.regular.forEach(recipe => {
      const source = getRecipeSource(recipe);
      if (!groups[source]) groups[source] = [];
      groups[source].push(recipe);
    });
    
    // Sort sources: "Honey Cooking Companion" first, then alphabetically
    const sortedSources = Object.keys(groups).sort((a, b) => {
      if (a === 'Honey Cooking Companion') return -1;
      if (b === 'Honey Cooking Companion') return 1;
      return a.localeCompare(b);
    });
    
    return sortedSources.map(source => ({
      key: source,
      label: source,
      emoji: source === 'Honey Cooking Companion' ? '🐝' : '🌐',
      recipes: groups[source]
    }));
  };

  // Group recipes by main ingredient for "By Ingredient" view
  const getGroupedByIngredient = () => {
    const groups = {};
    const uncategorized = [];
    
    filteredRecipes.regular.forEach(recipe => {
      const ingredients = recipe.main_ingredients;
      if (!ingredients || ingredients.length === 0) {
        uncategorized.push(recipe);
      } else {
        ingredients.forEach(ing => {
          if (!groups[ing]) groups[ing] = [];
          groups[ing].push(recipe);
        });
      }
    });
    
    const result = INGREDIENT_ORDER
      .filter(i => groups[i.key] && groups[i.key].length > 0)
      .map(i => ({ ...i, recipes: groups[i.key] }));
    
    if (uncategorized.length > 0) {
      result.push({ key: '_uncategorized', label: 'Uncategorized', recipes: uncategorized });
    }
    
    return result;
  };

  // Group recipes by main appliance for "By Appliance" view
  const getGroupedByAppliance = () => {
    const groups = {};
    const uncategorized = [];
    
    filteredRecipes.regular.forEach(recipe => {
      const appliances = recipe.main_appliances;
      if (!appliances || appliances.length === 0) {
        uncategorized.push(recipe);
      } else {
        appliances.forEach(app => {
          if (!groups[app]) groups[app] = [];
          groups[app].push(recipe);
        });
      }
    });
    
    const result = APPLIANCE_ORDER
      .filter(a => groups[a.key] && groups[a.key].length > 0)
      .map(a => ({ ...a, recipes: groups[a.key] }));
    
    if (uncategorized.length > 0) {
      result.push({ key: '_uncategorized', label: 'Uncategorized', recipes: uncategorized });
    }
    
    return result;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (cookedFilter !== 'all') count++;
    if (selectedLabel !== 'all') count++;
    return count;
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // Clear all auth-related storage including user cache
    localStorage.removeItem('session_token');
    clearUserCaches(); // Clear all user-specific caches to prevent cross-user data
    sessionStorage.clear();
    // Clear any cookies we can access
    document.cookie = 'session_token=; path=/; max-age=0; secure; samesite=none';
    // Redirect to login
    navigate('/login');
  };
  
  // Handle creating a new label
  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;
    setCreatingLabel(true);
    try {
      const res = await labelsApi.create(newLabelName.trim());
      setUserLabels([...userLabels, res.data.label]);
      setNewLabelName('');
    } catch (error) {
      console.error('Failed to create label:', error);
    } finally {
      setCreatingLabel(false);
    }
  };
  
  // Handle deleting a label
  const handleDeleteLabel = async (labelName) => {
    // Find the label to check if it's protected
    const label = userLabels.find(l => l.name === labelName);
    if (label?.protected) {
      alert('This label is protected and cannot be deleted.');
      return;
    }
    
    if (!window.confirm(`Delete label "${labelName}"? This will remove it from all recipes.`)) {
      return;
    }
    
    try {
      await labelsApi.delete(labelName);
      setUserLabels(userLabels.filter(l => l.name !== labelName));
      // If this label was selected in filter, reset to 'all'
      if (selectedLabel === labelName) {
        setSelectedLabel('all');
      }
      // Refresh recipes to update label display
      const recipesRes = await recipeApi.getAll({ include_deleted: showDeleted });
      setRecipes(recipesRes.data);
    } catch (error) {
      console.error('Failed to delete label:', error);
      alert('Failed to delete label. Please try again.');
    }
  };
  
  // Generate Mise en Place timeline
  const handleGenerateMiseEnPlace = async (forceRegenerate = false) => {
    if (!filteredRecipes.currentMeal || filteredRecipes.currentMeal.length === 0) return;
    
    const recipeIds = filteredRecipes.currentMeal.map(r => r.recipe_id);
    const cacheKey = [...recipeIds].sort().join(',');
    
    // If we have cached content for these recipes and not forcing regenerate, just show the modal
    if (miseEnPlaceContent && !forceRegenerate && cacheKey === miseEnPlaceCacheKeyRef.current) {
      setShowMiseEnPlace(true);
      return;
    }
    
    // Track analytics (only when actually generating, not just viewing cached)
    trackMiseEnPlaceGenerated(recipeIds.length);
    
    setMiseEnPlaceLoading(true);
    setShowMiseEnPlace(true);
    
    // Clear checked items when regenerating
    if (forceRegenerate) {
      setMiseCheckedItems({});
    }
    
    try {
      const response = await ai.generateMiseEnPlace(recipeIds);
      setMiseEnPlaceContent(response.data.timeline);
      miseEnPlaceCacheKeyRef.current = cacheKey;
    } catch (error) {
      console.error('Failed to generate mise en place:', error);
      setMiseEnPlaceContent('❌ Failed to generate timeline. Please try again.');
    } finally {
      setMiseEnPlaceLoading(false);
    }
  };

  // Print Shopping List (by aisle) for all Currently Cooking recipes
  const handlePrintShoppingList = async () => {
    if (!filteredRecipes.currentMeal || filteredRecipes.currentMeal.length === 0) return;
    
    // Fetch groceries (unchecked only)
    let groceryItems = [];
    try {
      const groceryResponse = await groceriesApi.get();
      groceryItems = (groceryResponse.data.items || []).filter(item => !item.checked);
    } catch (error) {
      console.error('Failed to fetch groceries for print:', error);
    }
    
    const recipes = filteredRecipes.currentMeal;
    const hasGroceries = groceryItems.length > 0;
    const numColumns = recipes.length + (hasGroceries ? 1 : 0);
    const isLandscape = numColumns >= 3;
    
    // Aisle categories with keywords (same as PrintModal.js)
    const AISLE_CATEGORIES = {
      produce: {
        label: '🥬 Produce',
        keywords: ['lettuce', 'spinach', 'kale', 'arugula', 'cabbage', 'broccoli', 'cauliflower', 'carrot', 'celery', 'onion', 'garlic', 'ginger', 'potato', 'tomato', 'pepper', 'bell pepper', 'jalapeño', 'cucumber', 'zucchini', 'squash', 'eggplant', 'mushroom', 'corn', 'peas', 'beans', 'asparagus', 'artichoke', 'avocado', 'lemon', 'lime', 'orange', 'apple', 'banana', 'berry', 'berries', 'strawberry', 'blueberry', 'raspberry', 'grape', 'melon', 'watermelon', 'mango', 'pineapple', 'peach', 'pear', 'plum', 'cherry', 'fig', 'pomegranate', 'herbs', 'basil', 'cilantro', 'parsley', 'mint', 'dill', 'thyme', 'rosemary', 'sage', 'oregano', 'chives', 'scallion', 'green onion', 'leek', 'shallot', 'fennel', 'radish', 'beet', 'turnip', 'sweet potato', 'yam']
      },
      dairy: {
        label: '🧊 Dairy & Refrigerated',
        keywords: ['milk', 'cream', 'half-and-half', 'butter', 'margarine', 'cheese', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'goat cheese', 'ricotta', 'cottage cheese', 'cream cheese', 'sour cream', 'yogurt', 'greek yogurt', 'egg', 'eggs', 'mayonnaise', 'mayo', 'tofu', 'tempeh']
      },
      deli: {
        label: '🥪 Deli',
        keywords: ['salami', 'pepperoni', 'prosciutto', 'ham', 'sliced turkey', 'bologna', 'pastrami', 'mortadella', 'capicola', 'sopressata', 'chorizo', 'hummus']
      },
      meat: {
        label: '🥩 Meat & Seafood',
        keywords: ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'duck', 'bacon', 'sausage', 'ground beef', 'ground turkey', 'ground pork', 'steak', 'roast', 'ribs', 'chop', 'tenderloin', 'breast', 'thigh', 'wing', 'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'halibut', 'trout', 'shrimp', 'prawn', 'crab', 'lobster', 'scallop', 'mussel', 'clam', 'oyster', 'squid', 'calamari', 'anchovy', 'anchovies', 'sardine']
      },
      bakery: {
        label: '🍞 Bakery & Bread',
        keywords: ['bread', 'loaf', 'baguette', 'roll', 'bun', 'croissant', 'bagel', 'pita', 'tortilla', 'wrap', 'naan', 'flatbread', 'crouton', 'breadcrumb', 'panko']
      },
      pantry: {
        label: '🫙 Pantry & Dry Goods',
        keywords: ['flour', 'sugar', 'brown sugar', 'powdered sugar', 'baking soda', 'baking powder', 'yeast', 'cornstarch', 'cocoa', 'chocolate', 'vanilla', 'extract', 'rice', 'pasta', 'noodle', 'spaghetti', 'penne', 'macaroni', 'quinoa', 'couscous', 'oat', 'oatmeal', 'cereal', 'granola', 'nut', 'almond', 'walnut', 'pecan', 'cashew', 'peanut', 'pistachio', 'seed', 'sesame', 'sunflower', 'chia', 'flax', 'lentil', 'chickpea', 'bean', 'black bean', 'kidney bean', 'cannellini', 'navy bean', 'pinto bean', 'canned', 'tomato paste', 'tomato sauce', 'diced tomato', 'crushed tomato', 'coconut milk', 'broth', 'stock', 'bouillon', 'honey', 'maple syrup', 'molasses', 'agave', 'jam', 'jelly', 'preserves', 'peanut butter', 'almond butter', 'tahini', 'pickle', 'olive', 'caper', 'cornmeal', 'polenta', 'raisin', 'dried fruit']
      },
      condiments: {
        label: '🍶 Oils, Vinegars & Condiments',
        keywords: ['oil', 'olive oil', 'vegetable oil', 'canola oil', 'coconut oil', 'sesame oil', 'avocado oil', 'vinegar', 'balsamic', 'red wine vinegar', 'white wine vinegar', 'apple cider vinegar', 'rice vinegar', 'soy sauce', 'tamari', 'fish sauce', 'worcestershire', 'hot sauce', 'sriracha', 'ketchup', 'mustard', 'dijon', 'relish', 'salsa', 'barbecue sauce', 'teriyaki', 'hoisin', 'oyster sauce', 'miso', 'curry paste', 'harissa', 'pesto']
      },
      spices: {
        label: '🧂 Spices & Seasonings',
        keywords: ['spice', 'seasoning', 'cumin', 'coriander', 'paprika', 'smoked paprika', 'cayenne', 'chili powder', 'curry powder', 'garam masala', 'turmeric', 'cinnamon', 'nutmeg', 'clove', 'allspice', 'cardamom', 'ginger powder', 'garlic powder', 'onion powder', 'oregano dried', 'basil dried', 'thyme dried', 'rosemary dried', 'bay leaf', 'bay leaves', 'italian seasoning', 'herbs de provence', 'sumac', 'saffron', 'vanilla bean']
      },
      frozen: {
        label: '❄️ Frozen',
        keywords: ['frozen', 'ice cream', 'sorbet', 'frozen vegetable', 'frozen fruit', 'frozen pizza', 'frozen meal']
      },
      beverages: {
        label: '🥤 Beverages',
        keywords: ['juice', 'orange juice', 'apple juice', 'lemonade', 'soda', 'wine', 'beer', 'liquor', 'vodka', 'rum', 'whiskey', 'bourbon', 'tequila', 'gin', 'brandy', 'coffee', 'tea', 'espresso']
      },
      staples: {
        label: '🏠 You May Already Have',
        keywords: []
      },
      other: {
        label: '📦 Other',
        keywords: []
      }
    };
    
    // Staples list
    const STAPLES = [
      'salt', 'black pepper', 'white pepper', 'kosher salt', 'sea salt',
      'olive oil', 'vegetable oil', 'canola oil', 'cooking spray',
      'flour', 'all-purpose flour', 'bread flour',
      'sugar', 'granulated sugar', 'brown sugar', 'powdered sugar',
      'baking soda', 'baking powder', 'cornstarch',
      'garlic powder', 'onion powder', 'paprika', 'cumin', 'cinnamon', 'oregano', 'basil dried', 'italian seasoning',
      'vanilla extract', 'vanilla',
      'soy sauce', 'worcestershire'
    ];
    
    const STAPLE_EXCLUDERS = ['bell', 'red', 'green', 'yellow', 'orange', 'jalapeño', 'cayenne', 'chili', 'hot'];
    
    // Priority order for checking aisles
    const AISLE_PRIORITY = ['dairy', 'condiments', 'spices', 'pantry', 'meat', 'bakery', 'frozen', 'beverages', 'produce'];
    
    // Check if ingredient is a staple
    const isStaple = (ingredientName) => {
      const nameLower = ingredientName.toLowerCase();
      const hasExcluder = STAPLE_EXCLUDERS.some(ex => nameLower.includes(ex));
      if (hasExcluder) return false;
      return STAPLES.some(staple => nameLower.includes(staple));
    };
    
    // Categorize ingredient into aisle
    const categorizeIngredient = (ingredientName) => {
      const nameLower = ingredientName.toLowerCase();
      
      // Check if it's a staple first
      if (isStaple(ingredientName)) {
        return 'staples';
      }
      
      // Check aisle categories in priority order
      for (const aisle of AISLE_PRIORITY) {
        const config = AISLE_CATEGORIES[aisle];
        if (!config) continue;
        for (const keyword of config.keywords) {
          if (nameLower.includes(keyword)) {
            return aisle;
          }
        }
      }
      return 'other';
    };
    
    // Aisle display order
    const aisleOrder = ['produce', 'dairy', 'deli', 'meat', 'bakery', 'pantry', 'condiments', 'spices', 'frozen', 'beverages', 'other', 'staples'];
    
    // Build a map of aisle -> recipe -> ingredients
    const aisleMap = {};
    
    recipes.forEach((recipe, recipeIdx) => {
      (recipe.ingredients || []).forEach(ing => {
        // Use AI-assigned aisle if valid, otherwise use keyword categorization
        let aisle = ing.aisle && AISLE_CATEGORIES[ing.aisle.toLowerCase()] 
          ? ing.aisle.toLowerCase() 
          : categorizeIngredient(ing.name);
        
        if (!aisleMap[aisle]) {
          aisleMap[aisle] = {};
        }
        if (!aisleMap[aisle][recipeIdx]) {
          aisleMap[aisle][recipeIdx] = [];
        }
        
        const formattedQty = formatQuantity(ing.quantity);
        const hasQty = formattedQty && formattedQty !== 'null';
        const hasUnit = ing.unit && ing.unit !== 'null';
        const abbrevUnit = hasUnit ? ({'teaspoon': 'tsp', 'teaspoons': 'tsp', 'tablespoon': 'Tbsp', 'tablespoons': 'Tbsp', 'ounce': 'oz', 'ounces': 'oz', 'pound': 'lb', 'pounds': 'lbs'}[ing.unit.toLowerCase()] || ing.unit) : '';
        const qtyText = (hasQty || abbrevUnit) ? `${hasQty ? formattedQty : ''} ${abbrevUnit}`.trim() + ' ' : '';
        
        aisleMap[aisle][recipeIdx].push(`${qtyText}${ing.name}`);
      });
    });
    
    // Add groceries to aisleMap (using 'groceries' as a special column key)
    const GROCERY_COL_KEY = 'groceries';
    groceryItems.forEach(item => {
      const aisle = item.aisle && AISLE_CATEGORIES[item.aisle.toLowerCase()] 
        ? item.aisle.toLowerCase() 
        : categorizeIngredient(item.name);
      
      if (!aisleMap[aisle]) {
        aisleMap[aisle] = {};
      }
      if (!aisleMap[aisle][GROCERY_COL_KEY]) {
        aisleMap[aisle][GROCERY_COL_KEY] = [];
      }
      aisleMap[aisle][GROCERY_COL_KEY].push(item.name);
    });
    
    // Sort aisles
    const sortedAisles = aisleOrder.filter(aisle => aisleMap[aisle]);
    
    // Calculate column width
    const colWidth = isLandscape ? Math.floor(100 / numColumns) : (numColumns === 2 ? 50 : 100);
    
    // Build HTML
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Shopping List</title>
        <style>
          @page { size: ${isLandscape ? 'landscape' : 'portrait'}; margin: 0.5in; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 12px;
            line-height: 1.3;
            padding: 15px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          h1 { font-size: 18px; margin-bottom: 10px; color: #d97706; }
          .header-row { 
            display: flex; 
            border-bottom: 2px solid #d97706; 
            padding-bottom: 8px; 
            margin-bottom: 12px;
          }
          .header-col { 
            width: ${colWidth}%; 
            font-weight: 600; 
            font-size: 13px;
            padding-right: 10px;
          }
          .aisle-section { margin-bottom: 12px; }
          .aisle-header { 
            font-size: 11px; 
            text-transform: uppercase; 
            letter-spacing: 0.5px; 
            color: #d97706; 
            font-weight: 600;
            margin-bottom: 4px;
            padding-top: 8px;
            border-top: 1px solid #e5e7eb;
          }
          .aisle-section:first-of-type .aisle-header { border-top: none; padding-top: 0; }
          .ingredients-row { display: flex; }
          .ingredients-col { 
            width: ${colWidth}%; 
            padding-right: 10px;
            vertical-align: top;
          }
          .ingredient-item { 
            padding: 1px 0;
            font-size: 12px;
          }
          .ingredient-item::before { content: "☐ "; }
          /* Floating return button */
          .return-btn { 
            position: fixed; 
            bottom: 20px; 
            right: 20px; 
            background: linear-gradient(135deg, #f59e0b, #d97706); 
            color: white; 
            border: none; 
            padding: 12px 20px; 
            border-radius: 50px; 
            font-size: 14px; 
            font-weight: 600; 
            cursor: pointer; 
            box-shadow: 0 4px 15px rgba(217, 119, 6, 0.4); 
            display: flex; 
            align-items: center; 
            gap: 8px;
            transition: transform 0.2s, box-shadow 0.2s;
            z-index: 9999;
          }
          .return-btn:hover { 
            transform: translateY(-2px); 
            box-shadow: 0 6px 20px rgba(217, 119, 6, 0.5); 
          }
          @media print { .return-btn { display: none; } }
          .grocery-header { color: #059669; }
          .grocery-item::before { content: "☐ "; color: #059669; }
        </style>
      </head>
      <body>
        <h1>🛒 Shopping List</h1>
        <div class="header-row">
          ${hasGroceries ? `<div class="header-col grocery-header">🛒 My Groceries</div>` : ''}
          ${recipes.map(r => `<div class="header-col">${r.title}</div>`).join('')}
        </div>
    `;
    
    sortedAisles.forEach(aisle => {
      const label = AISLE_CATEGORIES[aisle]?.label || aisle;
      html += `<div class="aisle-section">`;
      html += `<div class="aisle-header">${label}</div>`;
      html += `<div class="ingredients-row">`;
      
      // Add groceries column first
      if (hasGroceries) {
        const groceriesInAisle = aisleMap[aisle][GROCERY_COL_KEY] || [];
        html += `<div class="ingredients-col">`;
        groceriesInAisle.forEach(item => {
          html += `<div class="ingredient-item grocery-item">${item}</div>`;
        });
        html += `</div>`;
      }
      
      recipes.forEach((recipe, idx) => {
        const ingredients = aisleMap[aisle][idx] || [];
        html += `<div class="ingredients-col">`;
        ingredients.forEach(ing => {
          html += `<div class="ingredient-item">${ing}</div>`;
        });
        html += `</div>`;
      });
      
      html += `</div></div>`;
    });
    
    html += `
        <button class="return-btn" onclick="window.close();">
          🍯 Return to Honey
        </button>
      </body></html>`;
    
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
  };

  // Print All Recipes with page breaks
  const handlePrintAllRecipes = () => {
    if (!filteredRecipes.currentMeal || filteredRecipes.currentMeal.length === 0) return;
    
    const recipes = filteredRecipes.currentMeal;
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Currently Cooking Recipes</title>
        <style>
          @page { margin: 0.5in; }
          * { box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            max-width: 1000px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          .recipe { 
            page-break-after: always; 
            padding: 20px 0;
          }
          .recipe:last-child { page-break-after: avoid; }
          h1 { 
            font-size: 22px; 
            margin-bottom: 8px; 
            color: #222;
            border-bottom: 2px solid #d97706;
            padding-bottom: 8px;
          }
          .description { 
            color: #666; 
            margin-bottom: 16px; 
            font-style: italic;
          }
          .meta { 
            display: flex; 
            gap: 20px; 
            margin-bottom: 24px; 
            font-size: 14px;
            color: #555;
            flex-wrap: wrap;
          }
          .meta span { display: flex; align-items: center; gap: 4px; }
          /* Two-column layout for ingredients and instructions */
          .recipe-grid {
            display: grid;
            grid-template-columns: 35% 65%;
            gap: 40px;
          }
          .ingredients-col h2, .instructions-col h2 { 
            font-size: 14px; 
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 0; 
            margin-bottom: 16px; 
            color: #d97706;
            border-bottom: 2px solid #d97706;
            padding-bottom: 8px;
          }
          .ingredients-list { 
            padding-left: 20px;
            line-height: 1.8;
            margin: 0;
            list-style-type: disc;
          }
          .ingredients-list li { 
            margin-bottom: 8px;
          }
          .instructions-list { 
            padding-left: 0;
            line-height: 1.8;
            margin: 0;
            list-style-type: none;
          }
          .instructions-list li { 
            margin-bottom: 16px;
          }
          .tips { 
            background: #fef3c7; 
            padding: 20px; 
            margin-top: 16px;
            border-radius: 8px;
          }
          .tips h2 { 
            font-size: 14px; 
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 0; 
            margin-bottom: 12px; 
            color: #d97706;
            border: none;
            padding: 0;
          }
          .tips ul { margin: 0; }
          .notes { 
            margin-top: 20px;
            padding: 16px;
            border: 1px dashed #d97706;
            border-radius: 8px;
          }
          .notes h2 { margin-top: 0; font-size: 14px; color: #d97706; }
          /* Floating return button */
          .return-btn { 
            position: fixed; 
            bottom: 20px; 
            right: 20px; 
            background: linear-gradient(135deg, #f59e0b, #d97706); 
            color: white; 
            border: none; 
            padding: 12px 20px; 
            border-radius: 50px; 
            font-size: 14px; 
            font-weight: 600; 
            cursor: pointer; 
            box-shadow: 0 4px 15px rgba(217, 119, 6, 0.4); 
            display: flex; 
            align-items: center; 
            gap: 8px;
            transition: transform 0.2s, box-shadow 0.2s;
            z-index: 9999;
          }
          .return-btn:hover { 
            transform: translateY(-2px); 
            box-shadow: 0 6px 20px rgba(217, 119, 6, 0.5); 
          }
          @media print { 
            body { padding: 20px; max-width: 100%; }
            .recipe-grid { grid-template-columns: 35% 65%; }
            .return-btn { display: none; } 
          }
        </style>
      </head>
      <body>
    `;
    
    recipes.forEach(recipe => {
      const abbrevUnit = (unit) => {
        if (!unit || unit === 'null') return '';
        const abbrevs = {'teaspoon': 'tsp', 'teaspoons': 'tsp', 'tablespoon': 'Tbsp', 'tablespoons': 'Tbsp', 'ounce': 'oz', 'ounces': 'oz', 'pound': 'lb', 'pounds': 'lbs'};
        return abbrevs[unit.toLowerCase()] || unit;
      };
      
      // Build tips list if available
      const tipsList = recipe.recipe_tips?.map(tip => `<li>${tip}</li>`).join('') || '';
      
      html += `<div class="recipe">`;
      html += `<h1>${recipe.title}</h1>`;
      
      if (recipe.description) {
        html += `<p class="description">${recipe.description}</p>`;
      }
      
      html += `<div class="meta">`;
      if (recipe.servings) html += `<span>🍽️ ${recipe.servings} servings</span>`;
      if (recipe.prep_time) html += `<span>⏱️ Prep: ${recipe.prep_time} min</span>`;
      if (recipe.cook_time) html += `<span>🔥 Cook: ${recipe.cook_time} min</span>`;
      html += `</div>`;
      
      // Two-column layout: Ingredients on left, Instructions on right
      html += `<div class="recipe-grid">`;
      
      // Left column - Ingredients
      html += `<div class="ingredients-col">`;
      html += `<h2>Ingredients</h2>`;
      html += `<ul class="ingredients-list">`;
      (recipe.ingredients || []).forEach(ing => {
        const qty = formatQuantity(ing.quantity);
        const hasQty = qty && qty !== 'null';
        const unit = abbrevUnit(ing.unit);
        const prep = ing.preparation ? ` (${ing.preparation})` : '';
        const qtyUnit = [hasQty ? qty : '', unit].filter(Boolean).join(' ');
        html += `<li>${qtyUnit ? `<strong>${qtyUnit}</strong> ` : ''}${ing.name}${prep}</li>`;
      });
      html += `</ul>`;
      html += `</div>`;
      
      // Right column - Instructions
      html += `<div class="instructions-col">`;
      html += `<h2>Preparation</h2>`;
      html += `<ol class="instructions-list">`;
      (recipe.instructions || []).forEach((inst, idx) => {
        const text = typeof inst === 'object' ? inst.text : inst;
        const duration = inst.duration_minutes ? ` <span style="color: #666; font-size: 0.9em;">(${inst.duration_minutes} min)</span>` : '';
        html += `<li><strong>${idx + 1}.</strong> ${text}${duration}</li>`;
      });
      html += `</ol>`;
      
      // Tips & Tricks section
      if (tipsList) {
        html += `<div class="tips"><h2>Tips & Tricks</h2><ul>${tipsList}</ul></div>`;
      }
      html += `</div>`;
      
      html += `</div>`; // Close recipe-grid
      
      if (recipe.notes) {
        html += `<div class="notes"><h2>My Notes</h2><p>${recipe.notes}</p></div>`;
      }
      
      html += `</div>`;
    });
    
    html += `
        <button class="return-btn" onclick="window.close();">
          🍯 Return to Honey
        </button>
      </body></html>`;
    
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
  };

  // Print Up Next Shopping List - uses same sophisticated format as Currently Cooking
  const handlePrintUpNextShoppingList = async () => {
    if (!filteredRecipes.upNext || filteredRecipes.upNext.length === 0) return;
    
    trackShoppingListPrinted(filteredRecipes.upNext.length);
    
    // Fetch groceries (unchecked only)
    let groceryItems = [];
    try {
      const groceryResponse = await groceriesApi.get();
      groceryItems = (groceryResponse.data.items || []).filter(item => !item.checked);
    } catch (error) {
      console.error('Failed to fetch groceries for print:', error);
    }
    
    const recipes = filteredRecipes.upNext;
    const hasGroceries = groceryItems.length > 0;
    const numColumns = recipes.length + (hasGroceries ? 1 : 0);
    const isLandscape = numColumns >= 3;
    
    // Reuse the same aisle categorization logic
    const AISLE_CATEGORIES = {
      produce: { label: '🥬 Produce', keywords: ['lettuce', 'spinach', 'kale', 'arugula', 'cabbage', 'broccoli', 'cauliflower', 'carrot', 'celery', 'onion', 'garlic', 'ginger', 'potato', 'tomato', 'pepper', 'bell pepper', 'jalapeño', 'cucumber', 'zucchini', 'squash', 'eggplant', 'mushroom', 'corn', 'peas', 'beans', 'asparagus', 'artichoke', 'avocado', 'lemon', 'lime', 'orange', 'apple', 'banana', 'berry', 'berries', 'strawberry', 'blueberry', 'raspberry', 'grape', 'melon', 'watermelon', 'mango', 'pineapple', 'peach', 'pear', 'plum', 'cherry', 'fig', 'pomegranate', 'herbs', 'basil', 'cilantro', 'parsley', 'mint', 'dill', 'thyme', 'rosemary', 'sage', 'oregano', 'chives', 'scallion', 'green onion', 'leek', 'shallot', 'fennel', 'radish', 'beet', 'turnip', 'sweet potato', 'yam'] },
      dairy: { label: '🧊 Dairy & Refrigerated', keywords: ['milk', 'cream', 'half-and-half', 'butter', 'margarine', 'cheese', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'goat cheese', 'ricotta', 'cottage cheese', 'cream cheese', 'sour cream', 'yogurt', 'greek yogurt', 'egg', 'eggs', 'mayonnaise', 'mayo', 'tofu', 'tempeh'] },
      deli: { label: '🥪 Deli', keywords: ['salami', 'pepperoni', 'prosciutto', 'ham', 'sliced turkey', 'bologna', 'pastrami', 'mortadella', 'capicola', 'sopressata', 'chorizo', 'hummus'] },
      meat: { label: '🥩 Meat & Seafood', keywords: ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'duck', 'bacon', 'sausage', 'ground beef', 'ground turkey', 'ground pork', 'steak', 'roast', 'ribs', 'chop', 'tenderloin', 'breast', 'thigh', 'wing', 'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'halibut', 'trout', 'shrimp', 'prawn', 'crab', 'lobster', 'scallop', 'mussel', 'clam', 'oyster', 'squid', 'calamari', 'anchovy', 'anchovies', 'sardine'] },
      bakery: { label: '🍞 Bakery & Bread', keywords: ['bread', 'loaf', 'baguette', 'roll', 'bun', 'croissant', 'bagel', 'pita', 'tortilla', 'wrap', 'naan', 'flatbread', 'crouton', 'breadcrumb', 'panko'] },
      pantry: { label: '🫙 Pantry & Dry Goods', keywords: ['flour', 'sugar', 'brown sugar', 'powdered sugar', 'baking soda', 'baking powder', 'yeast', 'cornstarch', 'cocoa', 'chocolate', 'vanilla', 'extract', 'rice', 'pasta', 'noodle', 'spaghetti', 'penne', 'macaroni', 'quinoa', 'couscous', 'oat', 'oatmeal', 'cereal', 'granola', 'nut', 'almond', 'walnut', 'pecan', 'cashew', 'peanut', 'pistachio', 'seed', 'sesame', 'sunflower', 'chia', 'flax', 'lentil', 'chickpea', 'bean', 'black bean', 'kidney bean', 'cannellini', 'navy bean', 'pinto bean', 'canned', 'tomato paste', 'tomato sauce', 'diced tomato', 'crushed tomato', 'coconut milk', 'broth', 'stock', 'bouillon', 'honey', 'maple syrup', 'molasses', 'agave', 'jam', 'jelly', 'preserves', 'peanut butter', 'almond butter', 'tahini', 'pickle', 'olive', 'caper', 'cornmeal', 'polenta', 'raisin', 'dried fruit'] },
      condiments: { label: '🍶 Oils, Vinegars & Condiments', keywords: ['oil', 'olive oil', 'vegetable oil', 'canola oil', 'coconut oil', 'sesame oil', 'avocado oil', 'vinegar', 'balsamic', 'red wine vinegar', 'white wine vinegar', 'apple cider vinegar', 'rice vinegar', 'soy sauce', 'tamari', 'fish sauce', 'worcestershire', 'hot sauce', 'sriracha', 'ketchup', 'mustard', 'dijon', 'relish', 'salsa', 'barbecue sauce', 'teriyaki', 'hoisin', 'oyster sauce', 'miso', 'curry paste', 'harissa', 'pesto'] },
      spices: { label: '🧂 Spices & Seasonings', keywords: ['spice', 'seasoning', 'cumin', 'coriander', 'paprika', 'smoked paprika', 'cayenne', 'chili powder', 'curry powder', 'garam masala', 'turmeric', 'cinnamon', 'nutmeg', 'clove', 'allspice', 'cardamom', 'ginger powder', 'garlic powder', 'onion powder', 'oregano dried', 'basil dried', 'thyme dried', 'rosemary dried', 'bay leaf', 'bay leaves', 'italian seasoning', 'herbs de provence', 'sumac', 'saffron', 'vanilla bean'] },
      frozen: { label: '❄️ Frozen', keywords: ['frozen', 'ice cream', 'sorbet', 'frozen vegetable', 'frozen fruit', 'frozen pizza', 'frozen meal'] },
      beverages: { label: '🥤 Beverages', keywords: ['juice', 'orange juice', 'apple juice', 'lemonade', 'soda', 'wine', 'beer', 'liquor', 'vodka', 'rum', 'whiskey', 'bourbon', 'tequila', 'gin', 'brandy', 'coffee', 'tea', 'espresso'] },
      staples: { label: '🏠 You May Already Have', keywords: [] },
      other: { label: '📦 Other', keywords: [] }
    };
    
    const STAPLES = ['salt', 'black pepper', 'white pepper', 'kosher salt', 'sea salt', 'olive oil', 'vegetable oil', 'canola oil', 'cooking spray', 'flour', 'all-purpose flour', 'bread flour', 'sugar', 'granulated sugar', 'brown sugar', 'powdered sugar', 'baking soda', 'baking powder', 'cornstarch', 'garlic powder', 'onion powder', 'paprika', 'cumin', 'cinnamon', 'oregano', 'basil dried', 'italian seasoning', 'vanilla extract', 'vanilla', 'soy sauce', 'worcestershire', 'water'];
    const STAPLE_EXCLUDERS = ['bell', 'red', 'green', 'yellow', 'orange', 'jalapeño', 'cayenne', 'chili', 'hot'];
    const AISLE_PRIORITY = ['dairy', 'deli', 'condiments', 'spices', 'pantry', 'meat', 'bakery', 'frozen', 'beverages', 'produce'];
    
    const isStaple = (ingredientName) => {
      const nameLower = ingredientName.toLowerCase();
      const hasExcluder = STAPLE_EXCLUDERS.some(ex => nameLower.includes(ex));
      if (hasExcluder) return false;
      return STAPLES.some(staple => nameLower.includes(staple));
    };
    
    const categorizeIngredient = (ingredientName) => {
      const nameLower = ingredientName.toLowerCase();
      if (isStaple(ingredientName)) return 'staples';
      for (const aisle of AISLE_PRIORITY) {
        const config = AISLE_CATEGORIES[aisle];
        if (!config) continue;
        for (const keyword of config.keywords) {
          if (nameLower.includes(keyword)) return aisle;
        }
      }
      return 'other';
    };
    
    const aisleOrder = ['produce', 'dairy', 'deli', 'meat', 'bakery', 'pantry', 'condiments', 'spices', 'frozen', 'beverages', 'other', 'staples'];
    const aisleMap = {};
    const GROCERY_COL_KEY = 'groceries';
    
    recipes.forEach((recipe, recipeIdx) => {
      (recipe.ingredients || []).forEach(ing => {
        let aisle = ing.aisle && AISLE_CATEGORIES[ing.aisle.toLowerCase()] ? ing.aisle.toLowerCase() : categorizeIngredient(ing.name);
        if (!aisleMap[aisle]) aisleMap[aisle] = {};
        if (!aisleMap[aisle][recipeIdx]) aisleMap[aisle][recipeIdx] = [];
        
        const formattedQty = formatQuantity(ing.quantity);
        const hasQty = formattedQty && formattedQty !== 'null';
        const hasUnit = ing.unit && ing.unit !== 'null';
        const abbrevUnit = hasUnit ? ({'teaspoon': 'tsp', 'teaspoons': 'tsp', 'tablespoon': 'Tbsp', 'tablespoons': 'Tbsp', 'ounce': 'oz', 'ounces': 'oz', 'pound': 'lb', 'pounds': 'lbs'}[ing.unit.toLowerCase()] || ing.unit) : '';
        const qtyText = (hasQty || abbrevUnit) ? `${hasQty ? formattedQty : ''} ${abbrevUnit}`.trim() + ' ' : '';
        aisleMap[aisle][recipeIdx].push(`${qtyText}${ing.name}`);
      });
    });
    
    // Add groceries to aisleMap
    groceryItems.forEach(item => {
      const aisle = item.aisle && AISLE_CATEGORIES[item.aisle.toLowerCase()] 
        ? item.aisle.toLowerCase() 
        : categorizeIngredient(item.name);
      
      if (!aisleMap[aisle]) aisleMap[aisle] = {};
      if (!aisleMap[aisle][GROCERY_COL_KEY]) aisleMap[aisle][GROCERY_COL_KEY] = [];
      aisleMap[aisle][GROCERY_COL_KEY].push(item.name);
    });
    
    const sortedAisles = aisleOrder.filter(aisle => aisleMap[aisle]);
    const colWidth = isLandscape ? Math.floor(100 / numColumns) : (numColumns === 2 ? 50 : 100);
    
    let html = `<!DOCTYPE html><html><head><title>Up Next - Shopping List</title>
      <style>
        @page { size: ${isLandscape ? 'landscape' : 'portrait'}; margin: 0.5in; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; line-height: 1.3; padding: 15px; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        h1 { font-size: 18px; margin-bottom: 10px; color: #3b82f6; }
        .header-row { display: flex; border-bottom: 2px solid #3b82f6; padding-bottom: 8px; margin-bottom: 12px; }
        .header-col { width: ${colWidth}%; font-weight: 600; font-size: 13px; padding-right: 10px; }
        .grocery-header { color: #059669; }
        .aisle-section { margin-bottom: 12px; }
        .aisle-header { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #3b82f6; font-weight: 600; margin-bottom: 4px; padding-top: 8px; border-top: 1px solid #e5e7eb; }
        .aisle-section:first-of-type .aisle-header { border-top: none; padding-top: 0; }
        .ingredients-row { display: flex; }
        .ingredients-col { width: ${colWidth}%; padding-right: 10px; vertical-align: top; }
        .ingredient-item { padding: 1px 0; font-size: 12px; }
        .ingredient-item::before { content: "☐ "; }
        .grocery-item::before { content: "☐ "; color: #059669; }
        .return-btn { position: fixed; bottom: 20px; right: 20px; background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; border: none; padding: 12px 20px; border-radius: 50px; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4); z-index: 9999; }
        @media print { .return-btn { display: none; } }
      </style></head><body>
      <h1>📋 Up Next - Shopping List</h1>
      <div class="header-row">${hasGroceries ? `<div class="header-col grocery-header">🛒 My Groceries</div>` : ''}${recipes.map(r => `<div class="header-col">${r.title}</div>`).join('')}</div>`;
    
    sortedAisles.forEach(aisle => {
      const label = AISLE_CATEGORIES[aisle]?.label || aisle;
      html += `<div class="aisle-section"><div class="aisle-header">${label}</div><div class="ingredients-row">`;
      // Add groceries column first
      if (hasGroceries) {
        const groceriesInAisle = aisleMap[aisle][GROCERY_COL_KEY] || [];
        html += `<div class="ingredients-col">`;
        groceriesInAisle.forEach(item => { html += `<div class="ingredient-item grocery-item">${item}</div>`; });
        html += `</div>`;
      }
      recipes.forEach((recipe, idx) => {
        const ingredients = aisleMap[aisle][idx] || [];
        html += `<div class="ingredients-col">`;
        ingredients.forEach(ing => { html += `<div class="ingredient-item">${ing}</div>`; });
        html += `</div>`;
      });
      html += `</div></div>`;
    });
    
    html += `<button class="return-btn" onclick="window.close();">🍯 Return to Honey</button></body></html>`;
    
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
    setShowUpNextPrintDropdown(false);
  };

  // Print Up Next All Recipes - uses same sophisticated format as Individual Recipe and Currently Cooking
  const handlePrintUpNextAllRecipes = () => {
    if (!filteredRecipes.upNext || filteredRecipes.upNext.length === 0) return;
    
    trackRecipePrinted('all_up_next');
    
    const recipes = filteredRecipes.upNext;
    
    let html = `<!DOCTYPE html><html><head><title>Up Next Recipes</title>
      <style>
        @page { margin: 0.5in; }
        * { box-sizing: border-box; }
        body { 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
          font-size: 14px; 
          line-height: 1.5; 
          max-width: 1000px;
          margin: 0 auto;
          padding: 40px 20px;
          color: #333;
        }
        .recipe { page-break-after: always; padding: 20px 0; }
        .recipe:last-child { page-break-after: avoid; }
        h1 { font-size: 22px; margin-bottom: 8px; color: #222; border-bottom: 2px solid #3b82f6; padding-bottom: 8px; }
        .description { color: #666; margin-bottom: 16px; font-style: italic; }
        .meta { display: flex; gap: 20px; margin-bottom: 24px; font-size: 14px; color: #555; flex-wrap: wrap; }
        .meta span { display: flex; align-items: center; gap: 4px; }
        /* Two-column layout for ingredients and instructions */
        .recipe-grid { display: grid; grid-template-columns: 35% 65%; gap: 40px; }
        .ingredients-col h2, .instructions-col h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin-top: 0; margin-bottom: 16px; color: #3b82f6; border-bottom: 2px solid #3b82f6; padding-bottom: 8px; }
        .ingredients-list { padding-left: 20px; line-height: 1.8; margin: 0; list-style-type: disc; }
        .ingredients-list li { margin-bottom: 8px; }
        .instructions-list { padding-left: 0; line-height: 1.8; margin: 0; list-style-type: none; }
        .instructions-list li { margin-bottom: 16px; }
        .tips { background: #dbeafe; padding: 20px; margin-top: 16px; border-radius: 8px; }
        .tips h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin-top: 0; margin-bottom: 12px; color: #3b82f6; border: none; padding: 0; }
        .tips ul { margin: 0; }
        .notes { margin-top: 20px; padding: 16px; border: 1px dashed #3b82f6; border-radius: 8px; }
        .notes h2 { margin-top: 0; font-size: 14px; color: #3b82f6; }
        .return-btn { position: fixed; bottom: 20px; right: 20px; background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; border: none; padding: 12px 20px; border-radius: 50px; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4); z-index: 9999; }
        @media print { 
          body { padding: 20px; max-width: 100%; }
          .recipe-grid { grid-template-columns: 35% 65%; }
          .return-btn { display: none; } 
        }
      </style></head><body>`;
    
    recipes.forEach(recipe => {
      const abbrevUnit = (unit) => {
        if (!unit || unit === 'null') return '';
        const abbrevs = {'teaspoon': 'tsp', 'teaspoons': 'tsp', 'tablespoon': 'Tbsp', 'tablespoons': 'Tbsp', 'ounce': 'oz', 'ounces': 'oz', 'pound': 'lb', 'pounds': 'lbs'};
        return abbrevs[unit.toLowerCase()] || unit;
      };
      
      // Build tips list if available
      const tipsList = recipe.recipe_tips?.map(tip => `<li>${tip}</li>`).join('') || '';
      
      html += `<div class="recipe"><h1>${recipe.title}</h1>`;
      if (recipe.description) html += `<p class="description">${recipe.description}</p>`;
      
      html += `<div class="meta">`;
      if (recipe.servings) html += `<span>🍽️ ${recipe.servings} servings</span>`;
      if (recipe.prep_time) html += `<span>⏱️ Prep: ${recipe.prep_time} min</span>`;
      if (recipe.cook_time) html += `<span>🔥 Cook: ${recipe.cook_time} min</span>`;
      html += `</div>`;
      
      // Two-column layout: Ingredients on left, Instructions on right
      html += `<div class="recipe-grid">`;
      
      // Left column - Ingredients
      html += `<div class="ingredients-col"><h2>Ingredients</h2><ul class="ingredients-list">`;
      (recipe.ingredients || []).forEach(ing => {
        const qty = formatQuantity(ing.quantity);
        const hasQty = qty && qty !== 'null';
        const unit = abbrevUnit(ing.unit);
        const prep = ing.preparation ? ` (${ing.preparation})` : '';
        const qtyUnit = [hasQty ? qty : '', unit].filter(Boolean).join(' ');
        html += `<li>${qtyUnit ? `<strong>${qtyUnit}</strong> ` : ''}${ing.name}${prep}</li>`;
      });
      html += `</ul></div>`;
      
      // Right column - Instructions
      html += `<div class="instructions-col"><h2>Preparation</h2><ol class="instructions-list">`;
      (recipe.instructions || []).forEach((inst, idx) => {
        const text = typeof inst === 'object' ? inst.text : inst;
        const duration = inst.duration_minutes ? ` <span style="color: #666; font-size: 0.9em;">(${inst.duration_minutes} min)</span>` : '';
        html += `<li><strong>${idx + 1}.</strong> ${text}${duration}</li>`;
      });
      html += `</ol>`;
      
      // Tips & Tricks section
      if (tipsList) {
        html += `<div class="tips"><h2>Tips & Tricks</h2><ul>${tipsList}</ul></div>`;
      }
      html += `</div>`;
      
      html += `</div>`; // Close recipe-grid
      
      if (recipe.notes) html += `<div class="notes"><h2>My Notes</h2><p>${recipe.notes}</p></div>`;
      html += `</div>`;
    });
    
    html += `<button class="return-btn" onclick="window.close();">🍯 Return to Honey</button></body></html>`;
    
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
    setShowUpNextPrintDropdown(false);
  };

  // Render a section of recipes (grid or list)
  const renderRecipeSection = (recipes, hideCategory = false) => {
    if (viewMode === 'grid') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <RecipeCard
              key={recipe.recipe_id}
              recipe={recipe}
              onClick={() => navigate(`/recipe/${recipe.recipe_id}`)}
              userLabels={userLabels}
              hideCategory={hideCategory}
              
            />
          ))}
        </div>
      );
    }
    return (
      <div className="bg-white rounded-lg shadow-sm divide-y">
        {recipes.map((recipe) => (
          <RecipeListItem
            key={recipe.recipe_id}
            recipe={recipe}
            onClick={() => navigate(`/recipe/${recipe.recipe_id}`)}
            userLabels={userLabels}
            hideCategory={hideCategory}
          />
        ))}
      </div>
    );
  };

  // Render recipes with sub-headings based on sort type
  const renderRecipesWithHeadings = () => {
    // Helper to render a collapsible group
    const renderCollapsibleGroup = (group, options = {}) => {
      const { hideCategory = false, showEmoji = false, showIcon = false } = options;
      const isCollapsed = collapsedSections.has(group.key);
      
      // Determine section icon
      let SectionIcon = null;
      if (showEmoji && categoryIcons[group.key]) {
        SectionIcon = categoryIcons[group.key].Icon;
      }
      // Time-period sections get a calendar icon
      const isTimePeriod = ['today', 'yesterday', 'this_week', 'this_month', 'last_3_months', 'older'].includes(group.key);
      if (isTimePeriod) {
        SectionIcon = Calendar;
      }
      
      return (
        <div key={group.key} data-testid={`group-${group.key}`}>
          <button
            type="button"
            onClick={() => toggleSection(group.key)}
            className="w-full text-left flex items-center gap-2 mb-4 group cursor-pointer"
            data-testid={`group-header-${group.key}`}
          >
            {SectionIcon && (
              <span className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                <SectionIcon size={16} className="text-amber-600" strokeWidth={1.5} />
              </span>
            )}
            {showIcon && group.icon && <span className="text-2xl">{group.icon}</span>}
            {showIcon && !group.icon && group.color && (
              <span className="w-5 h-5 rounded-full inline-block" style={{ backgroundColor: group.color }} />
            )}
            <h2 className="text-lg font-bold text-gray-900">
              {group.label}
            </h2>
            <span className="text-sm font-normal text-gray-400">({group.recipes.length})</span>
            <span className="ml-auto text-gray-400 group-hover:text-gray-600 transition-colors">
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
            </span>
          </button>
          {!isCollapsed && renderRecipeSection(group.recipes, hideCategory)}
        </div>
      );
    };

    // Helper to render all groups with collapse support
    const renderGroupedSections = (groups, options = {}) => {
      const allKeys = groups.map(g => g.key);
      const allCollapsed = allKeys.length > 0 && allKeys.every(k => collapsedSections.has(k));
      return (
        <div className="space-y-8" ref={groupedSectionsRef}>
          {groups.map((group) => renderCollapsibleGroup(group, options))}
        </div>
      );
    };

    if (sortBy === 'category') {
      const groups = getGroupedByCategory();
      return renderGroupedSections(groups, { hideCategory: true, showEmoji: true });
    }
    
    if (sortBy === 'last_viewed') {
      const groups = getGroupedByTimePeriod();
      return renderGroupedSections(groups);
    }
    
    if (sortBy === 'label') {
      const groups = getGroupedByLabel();
      return renderGroupedSections(groups, { showIcon: true });
    }
    
    if (sortBy === 'source') {
      const groups = getGroupedBySource();
      return renderGroupedSections(groups, { showEmoji: true });
    }
    
    if (sortBy === 'ingredient') {
      const groups = getGroupedByIngredient();
      return renderGroupedSections(groups);
    }
    
    if (sortBy === 'appliance') {
      const groups = getGroupedByAppliance();
      return renderGroupedSections(groups);
    }
    
    // Alphabetical (A-Z) - add header to separate from Up Next
    return (
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span>Recipes</span>
          <span className="text-sm font-normal text-gray-400">({filteredRecipes.regular.length})</span>
        </h2>
        {renderRecipeSection(filteredRecipes.regular)}
      </div>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading your recipes..." fullScreen />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/HoneyCookingCompanion_bee.svg" 
                alt="Honey Bee" 
                className="w-14 h-14 object-contain"
              />
              <img 
                src="/HoneyCookingCompanion_nobee.svg" 
                alt="Honey - Cooking Companion" 
                className="h-12 object-contain"
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => {
                    setShowCreateMenu(!showCreateMenu);
                    // Dismiss tip when user clicks the button
                    if (showNewRecipeTip) {
                      setShowNewRecipeTip(false);
                    }
                  }}
                  data-testid="create-recipe-button"
                  className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <Plus size={20} />
                  <span className="hidden sm:inline">New Recipe</span>
                </button>
                {/* New Recipe Tip - shows 3 times then auto-dismisses */}
                {showNewRecipeTip && recipes.length > 0 && !showCreateMenu && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-amber-200 p-3 z-50 animate-fade-in">
                    <button 
                      onClick={() => setShowNewRecipeTip(false)}
                      className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 text-sm"
                    >
                      ✕
                    </button>
                    <div className="flex items-start gap-2">
                      <span className="text-xl">✨</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-1">Add New Recipes Here!</p>
                        <p className="text-xs text-gray-600">
                          Tap to create recipes with <strong>AI Chat</strong> or import from your favorite <strong>recipe websites</strong>.
                        </p>
                      </div>
                    </div>
                    <div className="absolute -top-2 right-6 w-3 h-3 bg-white border-l border-t border-amber-200 transform rotate-45"></div>
                  </div>
                )}
                {showCreateMenu && (
                  <div className="create-menu absolute right-0 sm:left-0 mt-2 w-56 bg-white rounded-xl shadow-lg py-2 z-50 border border-gray-100">
                    <button
                      onClick={() => {
                        localStorage.removeItem('currentChatId');
                        navigate('/create');
                        setShowCreateMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-amber-50 transition-colors"
                    >
                      <span className="text-xl">💬</span>
                      <div className="text-left">
                        <p className="font-medium">AI Chat</p>
                        <p className="text-xs text-gray-500">Create with AI assistance</p>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/browse');
                        setShowCreateMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-amber-50 transition-colors"
                    >
                      <span className="text-xl">🌐</span>
                      <div className="text-left">
                        <p className="font-medium">Browse Websites</p>
                        <p className="text-xs text-gray-500">Import from recipe sites</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : user?.email ? (
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <span className="text-amber-700 font-medium text-sm">{user.email[0].toUpperCase()}</span>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
                  )}
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-100">
                    <div className="px-4 py-2 border-b">
                      <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                      <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                    </div>
                    {/* Measurement System Toggle */}
                    <div className="px-4 py-2 border-b" data-testid="measurement-toggle-menu">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Scale size={14} className="text-gray-400" />
                        <span className="text-xs text-gray-500">Measurements</span>
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          onClick={async () => {
                            setMeasurementSystem('imperial');
                            try { await userApi.updateMeasurementSystem('imperial'); } catch {}
                          }}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                            measurementSystem === 'imperial'
                              ? 'bg-amber-100 text-amber-800 border border-amber-300'
                              : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                          }`}
                          data-testid="measurement-imperial-btn"
                        >
                          US Standard
                        </button>
                        <button
                          onClick={async () => {
                            setMeasurementSystem('metric');
                            try { await userApi.updateMeasurementSystem('metric'); } catch {}
                          }}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                            measurementSystem === 'metric'
                              ? 'bg-amber-100 text-amber-800 border border-amber-300'
                              : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                          }`}
                          data-testid="measurement-metric-btn"
                        >
                          Metric
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowFeedbackModal(true);
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 text-sm"
                    >
                      <MessageCircle size={16} />
                      Help / Feedback
                    </button>
                    {user?.is_admin && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/admin');
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-amber-600 hover:bg-amber-50 text-sm font-medium"
                      >
                        <ChefHat size={16} />
                        Admin Dashboard
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/waggle-dance');
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-amber-600 hover:bg-amber-50 text-sm"
                    >
                      <span className="text-base">🐝</span>
                      Play Waggle Dance
                    </button>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/privacy');
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 text-sm"
                      >
                        <Shield size={16} />
                        Privacy Policy
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/terms');
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 text-sm"
                      >
                        <FileText size={16} />
                        Terms of Service
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/import');
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 text-sm"
                      >
                        <BookmarkPlus size={16} />
                        Share to Honey Setup
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/import-paprika');
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 text-sm"
                      >
                        <FileUp size={16} />
                        Import from Paprika
                      </button>
                    </div>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32">
        {/* How Honey Works - Onboarding Guide */}
        <HowHoneyWorks />
        
        {/* Search, Filters, and View Controls - Only show if user has recipes */}
        {recipes.length > 0 && (
        <>
        {/* Search and Filter */}
        <div className="mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                placeholder="Search saved recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                data-testid="recipe-search-input"
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  data-testid="clear-search-button"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(true)}
              data-testid="filter-button"
              className="md:hidden flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors relative text-slate-700"
            >
              <Filter size={20} />
              {getActiveFilterCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-amber-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFilterCount()}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* View Controls: Sort, View Toggle, Deleted Filter */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-10 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-amber-500 cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <option value="last_viewed">Recently Viewed</option>
              <option value="category">By Type</option>
              <option value="label">By Label</option>
              <option value="source">By Source</option>
              <option value="title">A-Z</option>
              <option value="ingredient">By Ingredient</option>
              <option value="appliance">By Appliance</option>
            </select>
            <SortAsc size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>

          {/* View Toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-amber-600 text-white' : 'bg-gray-50 text-slate-600 hover:bg-gray-100'}`}
              title="Grid view"
            >
              <Grid size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-amber-600 text-white' : 'bg-gray-50 text-slate-600 hover:bg-gray-100'}`}
              title="List view"
            >
              <List size={20} />
            </button>
          </div>

          {/* Expand/Collapse All Button - only for grouped sort modes */}
          {hasGroupedSections && (
            <button
              onClick={() => {
                // Get all current group keys to determine state
                let allKeys = [];
                if (sortBy === 'category') allKeys = getGroupedByCategory().map(g => g.key);
                else if (sortBy === 'last_viewed') allKeys = getGroupedByTimePeriod().map(g => g.key);
                else if (sortBy === 'label') allKeys = getGroupedByLabel().map(g => g.key);
                else if (sortBy === 'source') allKeys = getGroupedBySource().map(g => g.key);
                else if (sortBy === 'ingredient') allKeys = getGroupedByIngredient().map(g => g.key);
                else if (sortBy === 'appliance') allKeys = getGroupedByAppliance().map(g => g.key);
                
                const allCollapsed = allKeys.length > 0 && allKeys.every(k => collapsedSections.has(k));
                if (allCollapsed) {
                  expandAllSections();
                } else {
                  collapseAllSections(allKeys);
                }
              }}
              className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-slate-600 hover:bg-gray-100 transition-colors"
              title={collapsedSections.size > 0 ? "Expand all sections" : "Collapse all sections"}
              data-testid="expand-collapse-all-button"
            >
              <ChevronsUpDown size={20} />
            </button>
          )}
        </div>

        {/* Desktop Filters */}
        <div className="hidden md:flex gap-4 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-100">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Categories</option>
              <option value="beverage">Beverages</option>
              <option value="appetizer">Appetizers</option>
              <option value="soup">Soups & Stews</option>
              <option value="salad">Salads</option>
              <option value="main_dish">Main Dishes</option>
              <option value="side">Sides</option>
              <option value="dessert">Desserts</option>
              <option value="breakfast">Breakfast & Brunch</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">Label</label>
            <select
              value={selectedLabel}
              onChange={(e) => setSelectedLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Labels</option>
              {userLabels.map((label) => (
                <option key={label.name} value={label.name}>
                  {label.icon ? `${label.icon} ` : ''}{label.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowManageLabelsModal(true)}
              className="mt-2 text-xs text-amber-600 hover:text-amber-700 hover:underline flex items-center gap-1"
            >
              <Tag size={12} />
              Manage Labels
            </button>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
            <select
              value={cookedFilter}
              onChange={(e) => setCookedFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Recipes</option>
              <option value="cooked">Cooked</option>
              <option value="not_cooked">Not Cooked Yet</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">Other</label>
            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <Trash2 size={16} className="text-gray-400" />
              <span className="text-sm text-gray-700">Show Deleted</span>
            </label>
          </div>
        </div>
        </>
        )}

        {/* Current Meal Section - Shows when there are recipes OR help tile is visible */}
        {((filteredRecipes.currentMeal && filteredRecipes.currentMeal.length > 0) || showCookingHelpTile) && (
          <div className="mb-8" data-section="currently-cooking">
            <div className="flex flex-wrap items-center gap-2 mb-4 pb-2 border-b-2 border-amber-600">
              <div className="flex items-center gap-2 min-w-0">
                <ChefHat size={20} className="text-amber-600 flex-shrink-0" />
                <h2 className="text-lg font-bold text-gray-900 whitespace-nowrap">Currently Cooking</h2>
              </div>
              {/* Action buttons - always visible, grayed out when no recipes */}
              <div className="flex items-center gap-1.5 sm:gap-2 ml-auto flex-wrap justify-end relative">
                {/* Actions label when help tile is shown */}
                {showCookingHelpTile && (
                  <span className="text-xs text-amber-600 font-medium mr-1 animate-pulse">Actions →</span>
                )}
                {/* Reorder button - always visible, grayed out when < 2 recipes */}
                <button
                  onClick={filteredRecipes.currentMeal?.length > 1 ? openCurrentlyReorderModal : undefined}
                  disabled={!filteredRecipes.currentMeal?.length || filteredRecipes.currentMeal.length < 2}
                  className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                    filteredRecipes.currentMeal?.length > 1 
                      ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Reorder recipes"
                >
                  <GripVertical size={16} />
                </button>
                {/* Clear button */}
                <button
                  onClick={filteredRecipes.currentMeal?.length > 0 ? handleClearCurrentlyKitchen : undefined}
                  disabled={!filteredRecipes.currentMeal?.length}
                  className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                    filteredRecipes.currentMeal?.length > 0 
                      ? 'bg-gray-100 text-slate-700 hover:bg-red-100 hover:text-red-600' 
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Clear all from Currently Cooking"
                >
                  <span className="font-medium">{filteredRecipes.currentMeal?.length || 0}</span>
                  <X size={16} />
                </button>
                {/* Shopping List button */}
                <button
                  onClick={filteredRecipes.currentMeal?.length > 0 ? () => {
                    trackShoppingListOpened(filteredRecipes.currentMeal.length);
                    navigate('/shopping-list', { state: { recipes: filteredRecipes.currentMeal, source: 'currentMeal' } });
                  } : undefined}
                  disabled={!filteredRecipes.currentMeal?.length}
                  className={`flex items-center gap-1.5 px-3 sm:px-3.5 py-2 text-sm rounded-lg transition-colors ${
                    filteredRecipes.currentMeal?.length > 0 
                      ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Interactive shopping list"
                >
                  <ShoppingCart size={18} />
                </button>
                {/* Mise en Place button */}
                <button
                  onClick={filteredRecipes.currentMeal?.length > 0 ? () => handleGenerateMiseEnPlace(false) : undefined}
                  disabled={!filteredRecipes.currentMeal?.length}
                  className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                    filteredRecipes.currentMeal?.length > 0 
                      ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title={filteredRecipes.currentMeal?.length > 0 
                    ? (miseEnPlaceContent ? "View cooking timeline (cached)" : "Generate cooking timeline")
                    : "Generate cooking timeline"
                  }
                >
                  <Timer size={16} />
                  {miseEnPlaceContent && !miseEnPlaceBackgroundLoading && filteredRecipes.currentMeal?.length > 0 && <span className="text-green-600">✓</span>}
                </button>
                
                {/* Print dropdown for Currently Cooking */}
                <div className="relative">
                  <button
                    onClick={filteredRecipes.currentMeal?.length > 0 ? () => setShowPrintDropdown(!showPrintDropdown) : undefined}
                    disabled={!filteredRecipes.currentMeal?.length}
                    className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                      filteredRecipes.currentMeal?.length > 0 
                        ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    title="Print options"
                  >
                    <Printer size={16} />
                    <ChevronDown size={14} />
                  </button>
                  {showPrintDropdown && filteredRecipes.currentMeal?.length > 0 && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setShowPrintDropdown(false)} />
                      <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-40">
                        <button
                          onClick={() => {
                            trackShoppingListPrinted(filteredRecipes.currentMeal.length);
                            handlePrintShoppingList();
                            setShowPrintDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2"
                        >
                          <List size={16} />
                          Shopping List (by aisle)
                        </button>
                        <button
                          onClick={() => {
                            trackRecipePrinted('all_currently_cooking');
                            handlePrintAllRecipes();
                            setShowPrintDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg flex items-center gap-2"
                        >
                          <ChefHat size={16} />
                          All Recipes
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-3'}>
              {/* Cooking Help Tile */}
              {showCookingHelpTile && (
                <CookingHelpTile 
                  viewMode={viewMode} 
                  onDismiss={handleDismissCookingHelpTile} 
                />
              )}
              {filteredRecipes.currentMeal?.map((recipe) => (
                <div 
                  key={recipe.recipe_id} 
                  className={`relative cursor-grab active:cursor-grabbing ${draggedItem === recipe.recipe_id ? 'opacity-50' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, recipe.recipe_id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, recipe.recipe_id)}
                  onDragEnd={handleDragEnd}
                >
                  {viewMode === 'grid' ? (
                    <RecipeCard 
                      recipe={recipe} 
                      onClick={() => navigate(`/recipe/${recipe.recipe_id}`, { state: { recipe } })}
                      showCurrentMealBadge={true}
                      userLabels={userLabels}
                      
                    />
                  ) : (
                    <RecipeListItem 
                      recipe={recipe} 
                      onClick={() => navigate(`/recipe/${recipe.recipe_id}`, { state: { recipe } })}
                      showCurrentMealBadge={true}
                      userLabels={userLabels}
                    />
                  )}
                  {/* Remove from Current Meal button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleCurrentMeal(recipe.recipe_id);
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors z-10"
                    title="Remove from Currently Cooking"
                  >
                    <X size={16} className="text-gray-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Up Next Section - Shows when there are recipes OR help tile is visible */}
        {((filteredRecipes.upNext && filteredRecipes.upNext.length > 0) || showUpNextHelpTile) && (
          <div className="mb-8 scroll-mt-24" data-section="up-next">
            <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-blue-500 gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-shrink">
                <List size={20} className="text-blue-600 flex-shrink-0" />
                <h2 className="text-lg font-bold text-gray-900 whitespace-nowrap">Up Next</h2>
              </div>
              {/* Action buttons - always visible, grayed out when no recipes */}
              <div className="relative flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                {/* Actions label when help tile is shown */}
                {showUpNextHelpTile && (
                  <span className="text-xs text-blue-600 font-medium mr-1 animate-pulse">Actions →</span>
                )}
                {/* Reorder button - always visible, grayed out when < 2 recipes */}
                <button
                  onClick={filteredRecipes.upNext?.length > 1 ? openUpNextReorderModal : undefined}
                  disabled={!filteredRecipes.upNext?.length || filteredRecipes.upNext.length < 2}
                  className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                    filteredRecipes.upNext?.length > 1 
                      ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Reorder recipes"
                >
                  <GripVertical size={16} />
                </button>
                {/* Count with X to clear all */}
                <button
                  data-testid="clear-up-next-btn"
                  disabled={!filteredRecipes.upNext?.length}
                  onClick={filteredRecipes.upNext?.length > 0 ? async () => {
                    console.log('Clear Up Next clicked');
                    if (window.confirm(`Remove all ${filteredRecipes.upNext.length} recipes from Up Next?`)) {
                      try {
                        for (const recipe of filteredRecipes.upNext) {
                          await recipeApi.toggleUpNext(recipe.recipe_id, false);
                        }
                        const updatedRecipes = recipes.map(r => 
                          r.up_next ? { ...r, up_next: false } : r
                        );
                        setRecipes(updatedRecipes);
                        try {
                          localStorage.setItem('recipesCache', JSON.stringify(createLightweightCache(updatedRecipes)));
                        } catch (e) {}
                      } catch (err) {
                        console.error('Failed to clear Up Next:', err);
                      }
                    }
                  } : undefined}
                  className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                    filteredRecipes.upNext?.length > 0 
                      ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Clear all from Up Next"
                >
                  <span className="font-medium">{filteredRecipes.upNext?.length || 0}</span>
                  <X size={14} className={filteredRecipes.upNext?.length > 0 ? "text-gray-400" : "text-gray-400"} />
                </button>
                {/* Shopping List button */}
                <div className="relative">
                  <button
                    onClick={filteredRecipes.upNext?.length > 0 ? () => {
                      trackShoppingListOpened(filteredRecipes.upNext.length);
                      navigate('/shopping-list', { state: { recipes: filteredRecipes.upNext, source: 'upNext' } });
                      // Dismiss tip when clicked
                      if (showUpNextShoppingTip) {
                        setShowUpNextShoppingTip(false);
                        localStorage.setItem('hasSeenUpNextShoppingTip', 'true');
                      }
                    } : undefined}
                    disabled={!filteredRecipes.upNext?.length}
                    className={`flex items-center gap-1.5 px-3 sm:px-3.5 py-2 text-sm rounded-lg transition-colors ${
                      filteredRecipes.upNext?.length > 0 
                        ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    title="Shopping list for Up Next"
                  >
                    <ShoppingCart size={18} className={(showUpNextShoppingTip || pulseShoppingIcon) ? 'animate-pulse' : ''} />
                  </button>
                  {/* Up Next Shopping List Tip - shows first time a recipe is added to Up Next */}
                  {showUpNextShoppingTip && filteredRecipes.upNext?.length > 0 && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-blue-200 p-3 z-50 animate-fade-in">
                      <button 
                        onClick={() => {
                          setShowUpNextShoppingTip(false);
                          localStorage.setItem('hasSeenUpNextShoppingTip', 'true');
                        }}
                        className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 text-sm"
                      >
                        ✕
                      </button>
                      <div className="flex items-start gap-2">
                        <span className="text-xl">🛒</span>
                        <div>
                          <p className="text-sm font-medium text-gray-800 mb-1">Shopping List Ready!</p>
                          <p className="text-xs text-gray-600">
                            Tap to view your <strong>Up Next recipe ingredients</strong> organized by aisle.
                          </p>
                        </div>
                      </div>
                      <div className="absolute -top-2 right-4 w-3 h-3 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
                    </div>
                  )}
                </div>
                
                {/* Print dropdown for Up Next */}
                <div className="relative">
                  <button
                    onClick={filteredRecipes.upNext?.length > 0 ? () => setShowUpNextPrintDropdown(!showUpNextPrintDropdown) : undefined}
                    disabled={!filteredRecipes.upNext?.length}
                    className={`flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                      filteredRecipes.upNext?.length > 0 
                        ? 'bg-gray-100 text-slate-700 hover:bg-gray-200' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    title="Print options"
                  >
                    <Printer size={16} />
                    <ChevronDown size={14} />
                  </button>
                  {showUpNextPrintDropdown && filteredRecipes.upNext?.length > 0 && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setShowUpNextPrintDropdown(false)} />
                      <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-40">
                        <button
                          onClick={handlePrintUpNextShoppingList}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2"
                        >
                          <List size={16} />
                          Shopping List (by aisle)
                        </button>
                        <button
                          onClick={handlePrintUpNextAllRecipes}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg flex items-center gap-2"
                        >
                          <List size={16} />
                          All Recipes
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-3'}>
              {/* Up Next Help Tile */}
              {showUpNextHelpTile && (
                <UpNextHelpTile 
                  viewMode={viewMode} 
                  onDismiss={handleDismissUpNextHelpTile} 
                />
              )}
              {filteredRecipes.upNext?.map((recipe) => (
                <div 
                  key={recipe.recipe_id} 
                  className={`relative cursor-grab active:cursor-grabbing ${draggedUpNextItem === recipe.recipe_id ? 'opacity-50' : ''}`}
                  draggable
                  onDragStart={(e) => handleUpNextDragStart(e, recipe.recipe_id)}
                  onDragOver={handleUpNextDragOver}
                  onDrop={(e) => handleUpNextDrop(e, recipe.recipe_id)}
                  onDragEnd={handleUpNextDragEnd}
                >
                  {viewMode === 'grid' ? (
                    <RecipeCard 
                      recipe={recipe} 
                      onClick={() => navigate(`/recipe/${recipe.recipe_id}`, { state: { recipe } })}
                      userLabels={userLabels}
                      
                    />
                  ) : (
                    <RecipeListItem 
                      recipe={recipe} 
                      onClick={() => navigate(`/recipe/${recipe.recipe_id}`, { state: { recipe } })}
                      userLabels={userLabels}
                    />
                  )}
                  {/* Remove from Up Next button */}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      
                      // Invalidate any in-flight fetches to prevent them from overwriting our update
                      fetchIdRef.current++;
                      
                      // Optimistic update
                      setRecipes(prev => prev.map(r => 
                        r.recipe_id === recipe.recipe_id ? { ...r, up_next: false } : r
                      ));
                      try {
                        await recipeApi.toggleUpNext(recipe.recipe_id, false);
                        // Update cache immediately after successful API call
                        try {
                          const cached = localStorage.getItem('recipesCache');
                          if (cached) {
                            const cachedRecipes = JSON.parse(cached);
                            const updatedCache = cachedRecipes.map(r => 
                              r.recipe_id === recipe.recipe_id ? { ...r, up_next: false } : r
                            );
                            localStorage.setItem('recipesCache', JSON.stringify(updatedCache));
                          }
                        } catch (cacheErr) {
                          // Ignore cache errors
                        }
                      } catch (err) {
                        // Revert on error
                        setRecipes(prev => prev.map(r => 
                          r.recipe_id === recipe.recipe_id ? { ...r, up_next: true } : r
                        ));
                        console.error('Failed to remove from Up Next:', err);
                      }
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors z-10"
                    title="Remove from Up Next"
                    data-testid="remove-from-upnext"
                  >
                    <X size={16} className="text-gray-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recipes with Headings */}
        {filteredRecipes.regular?.length === 0 && filteredRecipes.currentMeal?.length === 0 && filteredRecipes.upNext?.length === 0 ? (
          recipes.length === 0 ? (
            /* Welcome Card for Empty Recipe Box */
            <div className="max-w-lg mx-auto mt-8 mb-16">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <img 
                  src="/HoneyCookingCompanion_bee.svg" 
                  alt="Honey Bee" 
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-2">
                  Welcome to your Recipe Box!
                </h2>
                <p className="text-gray-600 mb-8">
                  Let's add your first recipe. How would you like to start?
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      localStorage.removeItem('currentChatId');
                      localStorage.setItem('chatStartMode', 'photo');
                      navigate('/create');
                    }}
                    className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                    data-testid="empty-state-photo-btn"
                  >
                    <span className="text-3xl">📸</span>
                    <span className="font-medium text-[#1e3a5f]">Snap a Photo</span>
                    <span className="text-xs text-gray-500">Recipe card or cookbook</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      localStorage.removeItem('currentChatId');
                      localStorage.setItem('chatStartMode', 'url');
                      navigate('/create');
                    }}
                    className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                    data-testid="empty-state-url-btn"
                  >
                    <span className="text-3xl">🔗</span>
                    <span className="font-medium text-[#1e3a5f]">Paste a URL</span>
                    <span className="text-xs text-gray-500">Import from any site</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      localStorage.removeItem('currentChatId');
                      localStorage.removeItem('chatStartMode');
                      navigate('/create');
                    }}
                    className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                    data-testid="empty-state-chat-btn"
                  >
                    <span className="text-3xl">💬</span>
                    <span className="font-medium text-[#1e3a5f]">Chat with AI</span>
                    <span className="text-xs text-gray-500">Describe what you want</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/browse')}
                    className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                    data-testid="empty-state-explore-btn"
                  >
                    <span className="text-3xl">🌐</span>
                    <span className="font-medium text-[#1e3a5f]">Import from Websites</span>
                    <span className="text-xs text-gray-500">Save recipes from any site</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* No Results Found (has recipes but filtered out) */
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                No recipes found
              </h2>
              <p className="text-gray-600 mb-6">
                Try adjusting your search or filters
              </p>
            </div>
          )
        ) : filteredRecipes.regular?.length > 0 ? (
          renderRecipesWithHeadings()
        ) : null}
      </main>

      {/* Mobile Filter Bottom Sheet */}
      <FilterBottomSheet
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        cookedFilter={cookedFilter}
        setCookedFilter={setCookedFilter}
        selectedLabel={selectedLabel}
        setSelectedLabel={setSelectedLabel}
        userLabels={userLabels}
        showDeleted={showDeleted}
        setShowDeleted={setShowDeleted}
        onManageLabels={() => {
          setShowFilters(false);
          setShowManageLabelsModal(true);
        }}
      />
      
      {/* Manage Labels Modal */}
      {showManageLabelsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Manage Labels</h3>
              <button
                onClick={() => setShowManageLabelsModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Labels List */}
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {userLabels.map((label) => (
                <div
                  key={label.name}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {label.icon ? (
                      <span className="text-lg">{label.icon}</span>
                    ) : (
                      <span 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: label.color }}
                      />
                    )}
                    <span className="font-medium text-gray-800">{label.name}</span>
                    {label.protected && (
                      <span className="text-xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
                        default
                      </span>
                    )}
                  </div>
                  {!label.protected && (
                    <button
                      onClick={() => handleDeleteLabel(label.name)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete label"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Create New Label */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Create new label
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  placeholder="e.g., Thanksgiving"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newLabelName.trim()) {
                      handleCreateLabel();
                    }
                  }}
                />
                <button
                  onClick={handleCreateLabel}
                  disabled={!newLabelName.trim() || creatingLabel}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
                >
                  {creatingLabel ? '...' : 'Add'}
                </button>
              </div>
            </div>
            
            {/* Close */}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowManageLabelsModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mise en Place Modal */}
      {showMiseEnPlace && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 mise-en-place-print-container print:bg-white print:p-0">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col mx-4 print:max-h-none print:shadow-none print:rounded-none">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <div className="flex items-center gap-2">
                <Timer size={24} className="text-amber-600" />
                <h3 className="text-lg font-semibold text-gray-900">Mise en Place</h3>
                {filteredRecipes.currentMeal && (
                  <span className="text-sm text-gray-500">
                    ({filteredRecipes.currentMeal.length} {filteredRecipes.currentMeal.length === 1 ? 'recipe' : 'recipes'})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 print:hidden">
                <button
                  onClick={() => {
                    const content = miseScrollRef.current;
                    if (!content) return;
                    
                    const clone = content.cloneNode(true);
                    clone.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.remove());
                    
                    const printWindow = window.open('', '_blank', 'width=900,height=700');
                    if (printWindow) {
                      printWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>Mise en Place - Cooking Timeline</title>
                          <style>
                            * { 
                              box-sizing: border-box; 
                              margin: 0; 
                              padding: 0;
                              -webkit-print-color-adjust: exact !important;
                              print-color-adjust: exact !important;
                              color-adjust: exact !important;
                            }
                            body { 
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                              padding: 15px;
                              background: white;
                              font-size: 13px;
                              line-height: 1.5;
                              color: #374151;
                              -webkit-print-color-adjust: exact !important;
                              print-color-adjust: exact !important;
                            }
                            .print-header {
                              display: flex;
                              align-items: center;
                              gap: 8px;
                              margin-bottom: 16px;
                              padding-bottom: 10px;
                              border-bottom: 2px solid #d97706;
                            }
                            .print-header h1 { font-size: 20px; font-weight: 600; color: #222; }
                            .print-header span { color: #666; font-size: 13px; }
                            
                            /* Phase sections */
                            h2, h3, h4 { font-size: 14px; font-weight: 600; color: #1f2937; margin: 12px 0 8px; }
                            h2 { font-size: 16px; border-bottom: 1px solid #fde68a; padding-bottom: 6px; }
                            h3 { font-size: 15px; color: #d97706; }
                            
                            /* Recipe names */
                            .recipe-name, strong { font-size: 13px; font-weight: 600; color: #b45309; }
                            
                            /* All text content */
                            p, span, div { font-size: 13px; }
                            
                            /* Lists */
                            ul { list-style: none; padding: 0; margin: 8px 0; }
                            li { 
                              font-size: 13px; 
                              margin-bottom: 6px; 
                              display: flex; 
                              gap: 8px; 
                              align-items: flex-start;
                              color: #374151;
                            }
                            
                            /* Ingredient pills - crucial for the nice formatting */
                            .inline-flex, 
                            span[class*="rounded-full"],
                            span.inline-flex {
                              display: inline-flex !important;
                              align-items: center;
                              padding: 2px 8px;
                              background-color: #e5e7eb !important;
                              color: #374151;
                              font-size: 11px;
                              border-radius: 9999px;
                              margin: 2px;
                              white-space: nowrap;
                            }
                            .bg-gray-200 {
                              background-color: #e5e7eb !important;
                            }
                            .rounded-full {
                              border-radius: 9999px !important;
                            }
                            .text-xs {
                              font-size: 11px !important;
                            }
                            .px-2 { padding-left: 8px; padding-right: 8px; }
                            .py-1 { padding-top: 4px; padding-bottom: 4px; }
                            .items-center { align-items: center; }
                            .flex-wrap { flex-wrap: wrap; }
                            .gap-1 { gap: 4px; }
                            .gap-2 { gap: 8px; }
                            .mt-2 { margin-top: 8px; }
                            
                            /* Step numbers */
                            .w-6, .h-6 {
                              width: 24px !important;
                              height: 24px !important;
                              min-width: 24px;
                            }
                            .bg-amber-600 {
                              background-color: #f59e0b !important;
                              color: white !important;
                              display: inline-flex;
                              align-items: center;
                              justify-content: center;
                              border-radius: 9999px;
                              font-size: 12px;
                              font-weight: 600;
                            }
                            
                            /* Background colors */
                            .bg-amber-50 { background-color: #fffbeb !important; }
                            .bg-gray-50 { background-color: #f9fafb !important; }
                            
                            /* Text colors */
                            .text-amber-600, .text-amber-700 { color: #d97706; }
                            .text-amber-800 { color: #92400e; }
                            .text-gray-500, .text-gray-600 { color: #6b7280; }
                            .text-gray-700 { color: #374151; }
                            .text-white { color: white; }
                            
                            /* Utilities */
                            .font-medium { font-weight: 500; }
                            .font-semibold { font-weight: 600; }
                            .font-bold { font-weight: 700; }
                            .text-sm { font-size: 12px; }
                            .border-l-4 { border-left: 3px solid #fbbf24; padding-left: 10px; }
                            .border-l-2 { border-left: 2px solid #fbbf24; }
                            .border-amber-400 { border-color: #fbbf24; }
                            .rounded-lg { border-radius: 6px; }
                            .p-3 { padding: 12px; }
                            .p-4 { padding: 16px; }
                            .pl-4 { padding-left: 16px; }
                            .mb-2 { margin-bottom: 8px; }
                            .mb-4 { margin-bottom: 16px; }
                            .mt-4 { margin-top: 16px; }
                            .space-y-2 > * + * { margin-top: 8px; }
                            .space-y-3 > * + * { margin-top: 12px; }
                            .flex { display: flex; }
                            .flex-shrink-0 { flex-shrink: 0; }
                            .items-start { align-items: flex-start; }
                            .gap-3 { gap: 12px; }
                            
                            /* Hide checkboxes in print */
                            input[type="checkbox"] { display: none; }
                            
                            /* Return button */
                            .return-btn { 
                              position: fixed; bottom: 20px; right: 20px; 
                              background: linear-gradient(135deg, #f59e0b, #d97706); 
                              color: white; border: none; padding: 12px 20px; 
                              border-radius: 50px; font-size: 14px; font-weight: 600; 
                              cursor: pointer; box-shadow: 0 4px 15px rgba(217, 119, 6, 0.4); 
                            }
                            @media print { .return-btn { display: none; } }
                          </style>
                        </head>
                        <body>
                          <div class="print-header">
                            <span style="font-size: 20px;">🍳</span>
                            <h1>Mise en Place</h1>
                            <span>(${filteredRecipes.currentMeal?.length || 0} recipes)</span>
                          </div>
                          ${clone.innerHTML}
                          <button class="return-btn" onclick="window.close();">🍯 Return to Honey</button>
                        </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.focus();
                      setTimeout(() => printWindow.print(), 250);
                    }
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Printer size={16} />
                  <span>Print</span>
                </button>
                <button
                  onClick={() => setShowMiseEnPlace(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div ref={miseScrollRef} className="flex-1 overflow-y-auto p-6 print:overflow-visible">
              {miseEnPlaceLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader size={32} className="animate-spin text-amber-600 mb-4" />
                  <p className="text-gray-600">Analyzing recipes and creating your timeline...</p>
                  <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
                </div>
              ) : loadingFullRecipes ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader size={32} className="animate-spin text-amber-600 mb-4" />
                  <p className="text-gray-600">Loading ingredient details...</p>
                </div>
              ) : typeof miseEnPlaceContent === 'string' && miseEnPlaceContent.startsWith('❌') ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">{miseEnPlaceContent}</p>
                </div>
              ) : miseEnPlaceContent ? (
                <MiseEnPlaceContent
                  content={miseEnPlaceContent}
                  currentMealRecipes={fullCurrentMealRecipes.length > 0 ? fullCurrentMealRecipes : filteredRecipes.currentMeal}
                  miseCheckedItems={miseCheckedItems}
                  setMiseCheckedItems={setMiseCheckedItems}
                  miseScrollRef={miseScrollRef}
                />
              ) : null}
            </div>
            
            {/* Footer */}
            <div className="p-4 pb-6 sm:pb-4 border-t flex justify-end print:hidden" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1rem))' }}>
              <button
                onClick={() => setShowMiseEnPlace(false)}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="p-6">
              {feedbackSent ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send size={28} className="text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600 mb-6">
                    We appreciate your feedback! Your message helps us make Honey better for everyone. 
                    We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => {
                      setShowFeedbackModal(false);
                      setFeedbackSent(false);
                      setFeedbackSubject('');
                      setFeedbackMessage('');
                    }}
                    className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Help / Feedback</h3>
                    <button
                      onClick={() => setShowFeedbackModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Have a question, suggestion, or found a bug? We'd love to hear from you!
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={feedbackSubject}
                        onChange={(e) => setFeedbackSubject(e.target.value)}
                        placeholder="e.g., Feature request, Bug report..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-600 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        value={feedbackMessage}
                        onChange={(e) => setFeedbackMessage(e.target.value)}
                        placeholder="Tell us what's on your mind..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-600 outline-none resize-none"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowFeedbackModal(false)}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        const subject = encodeURIComponent(feedbackSubject || 'Honey Feedback');
                        const body = encodeURIComponent(
                          `${feedbackMessage}\n\n---\nSent from Honey by ${user?.name || 'User'} (${user?.email || 'Unknown'})`
                        );
                        window.open(`mailto:honeycookingcompanion@gmail.com?subject=${subject}&body=${body}`, '_blank');
                        setFeedbackSent(true);
                      }}
                      disabled={!feedbackMessage.trim()}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Send size={16} />
                      Send Feedback
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Up Next Reorder Modal */}
      {showUpNextReorderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-slate-800">Reorder Up Next</h2>
              <button
                onClick={() => setShowUpNextReorderModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            {/* Reorder List */}
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-sm text-gray-500 mb-4 hidden sm:block">Drag to reorder or use the arrows</p>
              <p className="text-sm text-gray-500 mb-4 sm:hidden">Use the arrows to reorder</p>
              <div className="space-y-2">
                {reorderList.map((recipe, index) => (
                  <div
                    key={recipe.recipe_id}
                    draggable
                    onDragStart={(e) => handleReorderDragStart(e, index)}
                    onDragOver={(e) => handleReorderDragOver(e, index)}
                    onDragEnd={handleReorderDragEnd}
                    className={`flex items-center gap-3 p-3 bg-gray-50 rounded-xl border-2 transition-all ${
                      draggedReorderItem === index ? 'border-blue-500 bg-blue-50' : 'border-transparent'
                    }`}
                  >
                    {/* Drag Handle - Hidden on mobile */}
                    <div className="hidden sm:block cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none">
                      <GripVertical size={20} />
                    </div>
                    
                    {/* Recipe Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{recipe.title}</p>
                      <p className="text-xs text-gray-500">{recipe.category || 'Uncategorized'}</p>
                    </div>
                    
                    {/* Position Number */}
                    <span className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                      {index + 1}
                    </span>
                    
                    {/* Up/Down Arrows for Mobile */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => moveItemUp(index)}
                        disabled={index === 0}
                        className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'}`}
                      >
                        <ChevronDown size={16} className="rotate-180" />
                      </button>
                      <button
                        onClick={() => moveItemDown(index)}
                        disabled={index === reorderList.length - 1}
                        className={`p-1 rounded ${index === reorderList.length - 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'}`}
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t flex gap-3">
              <button
                onClick={() => setShowUpNextReorderModal(false)}
                className="flex-1 px-4 py-2.5 text-gray-600 bg-gray-100 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveReorderAndClose}
                className="flex-1 px-4 py-2.5 text-white bg-blue-500 rounded-xl font-medium hover:bg-blue-600 transition-colors"
              >
                Save Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Currently Cooking Reorder Modal */}
      {showCurrentlyReorderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-slate-800">Reorder Currently Cooking</h2>
              <button
                onClick={() => setShowCurrentlyReorderModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            {/* Reorder List */}
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-sm text-gray-500 mb-4 hidden sm:block">Drag to reorder or use the arrows</p>
              <p className="text-sm text-gray-500 mb-4 sm:hidden">Use the arrows to reorder</p>
              <div className="space-y-2">
                {currentlyReorderList.map((recipe, index) => (
                  <div
                    key={recipe.recipe_id}
                    draggable
                    onDragStart={(e) => handleCurrentlyReorderDragStart(e, index)}
                    onDragOver={(e) => handleCurrentlyReorderDragOver(e, index)}
                    onDragEnd={handleCurrentlyReorderDragEnd}
                    className={`flex items-center gap-3 p-3 bg-gray-50 rounded-xl border-2 transition-all ${
                      draggedCurrentlyReorderItem === index ? 'border-amber-600 bg-amber-50' : 'border-transparent'
                    }`}
                  >
                    {/* Drag Handle - Hidden on mobile */}
                    <div className="hidden sm:block cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none">
                      <GripVertical size={20} />
                    </div>
                    
                    {/* Recipe Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{recipe.title}</p>
                      <p className="text-xs text-gray-500">{recipe.category || 'Uncategorized'}</p>
                    </div>
                    
                    {/* Position Number */}
                    <span className="w-6 h-6 flex items-center justify-center bg-amber-100 text-amber-600 rounded-full text-sm font-medium">
                      {index + 1}
                    </span>
                    
                    {/* Up/Down Arrows for Mobile */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => moveCurrentlyItemUp(index)}
                        disabled={index === 0}
                        className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'}`}
                      >
                        <ChevronDown size={16} className="rotate-180" />
                      </button>
                      <button
                        onClick={() => moveCurrentlyItemDown(index)}
                        disabled={index === currentlyReorderList.length - 1}
                        className={`p-1 rounded ${index === currentlyReorderList.length - 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'}`}
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t flex gap-3">
              <button
                onClick={() => setShowCurrentlyReorderModal(false)}
                className="flex-1 px-4 py-2.5 text-gray-600 bg-gray-100 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCurrentlyReorderAndClose}
                className="flex-1 px-4 py-2.5 text-white bg-amber-600 rounded-xl font-medium hover:bg-amber-700 transition-colors"
              >
                Save Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide bottom nav when search is focused (keyboard open) */}
      {!isSearchFocused && <BottomNav />}
    </div>
  );
};

export default RecipeBox;
